import { Controller } from "stimulus";
import FroalaEditor from "froala-editor";
import "./froala-plugins";



export default class extends Controller {
  static targets = ["content", "editorWrapper"];

  connect() {
    this.blockId = this.element.dataset.blockId;
    this.blockType = 'rich_text';
    this.isReadOnly = this.data.get("readOnly") === "true";
    if (this.data.get("aiTextGenerator") && this.data.get("editAiContent")) {
      this.isReadOnly = false;
    }
    if (!this.isReadOnly) {
      this.richTextContentDivs = this.element.querySelectorAll(".rich-text-content");
      this.editorWrappers = this.element.querySelectorAll(".froala-editor-wrapper");
      this.timeout = null;
      this.hideTimeout = null;
      this.isUpdatingContent = false;
      this.isMouseDown = false;
      this.addEventListeners();
      
      // Initialize all editors on page load to avoid flickering
      this.initializeAllEditors();
    }
    
    // Setup real-time updates
    this.setupTaskBlockUpdateListener();
    
    // Make controller instance globally accessible for Froala events
    window.currentFroalaController = this;
    
    // Add global test method
    window.testHideEditor = () => this.testHideEditor();
    
    // Check CSRF token status
    this.checkCSRFToken();
  }

  addEventListeners() {
    this.richTextContentDivs.forEach((div, index) => {
      div.addEventListener("click", (event) => {
        
        
        const clickedElement = event.target;
        const isLink = clickedElement.tagName === 'A' || clickedElement.closest('a');
        
        
        const isModifierClick = event.ctrlKey || event.metaKey || event.button === 1;
        
        if (isLink && !isModifierClick) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
        }
        
        if (!isModifierClick) {
          this.showEditor(event, div);
        }
      }, { capture: true }); 
    });

    // Add click handler for editor wrapper to ensure focus
    this.editorWrappers.forEach(editorWrapper => {
      editorWrapper.addEventListener("click", (event) => {
        if (!editorWrapper.classList.contains("hidden")) {
          const textArea = editorWrapper.querySelector(".froala");
          if (textArea && textArea.froalaEditor) {
            textArea.froalaEditor.events.focus();
          }
        }
      });
    });

    // Track operation state to prevent premature hiding
    this.operationInProgress = false;

    document.addEventListener("mousedown", (event) => {
      this.editorWrappers.forEach(editorWrapper => {
        if (!editorWrapper.classList.contains("hidden")) {
          const isInEditor = editorWrapper.contains(event.target) ||
                            event.target.closest('.fr-element') ||
                            event.target.closest('.fr-box');
          if (isInEditor) {
            this.isMouseDown = true;
          }
        }
      });
    }, { capture: true });

    document.addEventListener("mouseup", (event) => {
      setTimeout(() => {
        this.isMouseDown = false;
      }, 10);
    }, { capture: true });

    document.addEventListener("click", (event) => {
      
      const isFrElementClick = event.target.classList && 
                               (event.target.classList.contains('fr-element') || 
                                event.target.closest('.fr-element'));
      if (isFrElementClick) {
        
      }
      
      // Check if click is on another rich text content div (switching editors)
      const clickedRichTextContent = event.target.closest('.rich-text-content');
      if (clickedRichTextContent && clickedRichTextContent !== event.target) {
        console.log('🔄 Click detected on another rich text content - switching editors');
        // Don't hide current editors, let the new editor show
        return;
      }
      
      this.editorWrappers.forEach(editorWrapper => {
        if (!editorWrapper.contains(event.target) && !editorWrapper.classList.contains("hidden")) {
          if (this.isMouseDown) {
            return;
          }
          
          const textArea = editorWrapper.querySelector(".froala");
          if (textArea && textArea.froalaEditor) {
            try {
              const selection = textArea.froalaEditor.selection.get();
              if (selection && !selection.collapsed) {
                return;
              }
            } catch (e) {
              
            }
          }
          
          if (this.isFroalaOperationInProgress(event)) {
            console.log('🔒 Preventing editor hide - Froala operation in progress');
            this.operationInProgress = true;
            return;
          }
          
          // Check if click is on any Froala UI elements that shouldn't hide the editor
          if (this.isFroalaUIElement(event.target)) {
            console.log('🔒 Preventing editor hide - clicking on Froala UI');
            this.operationInProgress = true;
            return;
          }
          
          // Check if click is on another editor's content div
          if (this.isClickingOnAnotherEditor(event.target)) {
            console.log('🔄 Click detected on another editor - preventing hide');
            return;
          }
          
          // If we get here, it's a genuine outside click
          console.log('✅ Genuine outside click detected, scheduling editor hide');
          this.scheduleEditorHide(editorWrapper);
        } else {
          console.log('🔍 Click is inside editor wrapper or editor is hidden');
        }
      });
    });
  }

  async initializeAllEditors() {
    console.log('🚀 Initializing all editors on page load...');
    
    // Fetch merge tags once for all editors
    let mergeTags = [];
    try {
      const firstEditorWrapper = this.editorWrappers[0];
      if (firstEditorWrapper) {
        mergeTags = await this.fetchMergeTags(firstEditorWrapper);
        console.log('✅ Merge tags fetched for all editors:', mergeTags.length);
      }
    } catch (error) {
      console.error('❌ Failed to fetch merge tags:', error);
    }
    
    // Initialize each editor wrapper
    this.editorWrappers.forEach((editorWrapper, index) => {
      console.log(`🔧 Initializing editor ${index + 1}/${this.editorWrappers.length}`);
      
      // Don't show the editor wrapper during initialization to prevent flickering
      // Keep it hidden and only show when user actually clicks on the content
      
      // Initialize Froala editor while keeping it hidden
      this.initializeFroala(editorWrapper, mergeTags);
    });
    
    console.log('✅ All editors initialized successfully!');
  }

  isFroalaOperationInProgress(event) {
    // Only check for truly active operations that should prevent hiding
    const hasLiquidTagDropdown = document.querySelector('.liquid-tag-quick-insert-dropdown') !== null;
    
    if (hasLiquidTagDropdown) {
      console.log('🔍 Liquid tag dropdown is open');
      return true;
    }
    
    return false;
  }

  isFroalaUIElement(target) {
    // Only check for elements that are truly part of the editor's active UI
    const isToolbar = target.closest('.fr-toolbar');
    const isQuickInsert = target.closest('.fr-quick-insert');
    const isLiquidTagDropdown = target.closest('.liquid-tag-quick-insert-dropdown');
    
    if (isToolbar || isQuickInsert || isLiquidTagDropdown) {
      console.log('🔍 Click is on Froala UI element:', {
        isToolbar: !!isToolbar,
        isQuickInsert: !!isQuickInsert,
        isLiquidTagDropdown: !!isLiquidTagDropdown
      });
      return true;
    }
    
    return false;
  }

  isClickingOnAnotherEditor(target) {
    // Check if the click is on another rich text content div
    const clickedRichTextContent = target.closest('.rich-text-content');
    if (clickedRichTextContent) {
      // Check if this rich text content belongs to a different editor
      const clickedEditorWrapper = clickedRichTextContent.nextElementSibling;
      if (clickedEditorWrapper && clickedEditorWrapper.classList.contains('froala-editor-wrapper')) {
        // This is a different editor, don't hide current ones
        return true;
      }
    }
    
    // Check if click is on any other editor wrapper
    const clickedEditorWrapper = target.closest('.froala-editor-wrapper');
    if (clickedEditorWrapper) {
      // This is a different editor wrapper, don't hide current ones
      return true;
    }
    
    return false;
  }

  markOperationComplete() {
    console.log('✅ Operation completed, editor can now be hidden');
    this.operationInProgress = false;
  }

  checkCSRFToken() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
    if (csrfToken) {
      console.log('✅ CSRF token found:', csrfToken.substring(0, 10) + '...');
      return true;
    } else {
      console.error('❌ CSRF token not found');
      return false;
    }
  }

  testHideEditor() {
    console.log('🧪 Testing hide editor functionality...');
    this.editorWrappers.forEach((editorWrapper, index) => {
      if (!editorWrapper.classList.contains("hidden")) {
        console.log(`🧪 Hiding editor ${index + 1}`);
        this.hideEditor(editorWrapper);
      } else {
        console.log(`🧪 Editor ${index + 1} is already hidden`);
      }
    });
  }

  showEditorOnClick(event) {
    const contentDiv = event.currentTarget;
    const clickedElement = event.target;
    const isLink = clickedElement.tagName === 'A' || clickedElement.closest('a');
    const isModifierClick = event.ctrlKey || event.metaKey || event.button === 1;
    
    if (isLink && !isModifierClick) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    if (!isModifierClick) {
      this.showEditor(event, contentDiv);
    }
  }

  showEditor(event, contentDiv) {
    if (this.isReadOnly) {
      return;
    }

    // Mark this block as actively being edited
    window.activeEditingManager.startEditing(this.blockId, this.blockType, this.element);

    // Cancel any scheduled editor hide to prioritize user interaction
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }

    if (contentDiv) {
      this.hideAllOtherEditors(contentDiv);
      
      const editorWrapper = contentDiv.nextElementSibling;

      if (editorWrapper && editorWrapper.classList.contains("froala-editor-wrapper")) {
        contentDiv.style.display = "none";
        contentDiv.style.visibility = "hidden";
        contentDiv.style.opacity = "0";
        
        editorWrapper.classList.remove("hidden");
        editorWrapper.style.display = "block";
        editorWrapper.style.visibility = "visible";
        editorWrapper.style.opacity = "1";
        editorWrapper.style.height = "auto";
        
        const textArea = editorWrapper.querySelector(".froala");
        
        if (textArea) {
          textArea.style.display = 'none';
          textArea.style.visibility = 'hidden';
        }
        
        const frElementToFocus = editorWrapper.querySelector('.fr-element');
        if (frElementToFocus && frElementToFocus !== textArea) {
          try {
            frElementToFocus.focus();
          } catch (e) {
            // Ignore
          }
        }
        
        const checkForEditor = (attempt = 1, maxAttempts = 2) => {
          let editor = null;
          if (textArea) {
            editor = textArea.froalaEditor;
            
            if (!editor && typeof jQuery !== 'undefined') {
              editor = jQuery(textArea).data('froala.editor');
            }
            
            if (!editor) {
              const frBox = editorWrapper.querySelector('.fr-box');
              if (frBox) {
                editor = frBox.froalaEditor;
                if (!editor && typeof jQuery !== 'undefined') {
                  editor = jQuery(frBox).data('froala.editor');
                }
                
                if (!editor) {
                  const frElement = editorWrapper.querySelector('.fr-element');
                  if (frElement && frElement !== textArea) {
                    editor = frElement.froalaEditor;
                    if (!editor && typeof jQuery !== 'undefined') {
                      editor = jQuery(frElement).data('froala.editor');
                    }
                  }
                }
                
                if (!editor && typeof FroalaEditor !== 'undefined' && FroalaEditor.get) {
                  try {
                    editor = FroalaEditor.get(textArea);
                  } catch (e) {
                    // Ignore errors
                  }
                }
                
                if (editor) {
                  textArea.froalaEditor = editor;
                  if (typeof jQuery !== 'undefined') {
                    jQuery(textArea).data('froala.editor', editor);
                  }
                }
              }
            }
          }
          
          if (editor) {
            return editor;
          } else if (attempt < maxAttempts) {
            setTimeout(() => checkForEditor(attempt + 1, maxAttempts), 50);
            return null;
          } else {
            return null;
          }
        };
        
        const handleEditorFound = (editor) => {
          if (!editor) return false;
          
          const contentDivHtml = contentDiv.innerHTML;
          if (contentDivHtml && contentDivHtml.trim() !== '' && 
              !contentDivHtml.includes('no-content') &&
              (contentDivHtml.includes('<') || contentDivHtml.includes('&'))) {
            const cleanHtml = this.formatContentForEditor(contentDivHtml);
            if (cleanHtml) {
              editor.html.set(cleanHtml);
            }
          }
          
          try {
            editor.events.focus();
            editor.selection.setAtEnd();
            editor.selection.restore();
            
            requestAnimationFrame(() => {
              try {
                const frElement = editor.el || (editor.$el && editor.$el[0]) || editorWrapper.querySelector('.fr-element');
                
                if (frElement) {
                  const clickEvent = new MouseEvent('click', {
                    bubbles: true,
                    cancelable: true,
                    view: window
                  });
                  frElement.dispatchEvent(clickEvent);
                }
                
                if (editor.quickInsert) {
                  editor.quickInsert._keepVisible = true;
                  editor.quickInsert._allowHide = false;
                  
                  if (editor.quickInsert.show) {
                    editor.quickInsert.show();
                  }
                  
                  const quickInsertElement = editor.$box ? editor.$box.find('.fr-quick-insert').get(0) : null;
                  if (quickInsertElement) {
                    quickInsertElement.classList.add('fr-visible');
                    quickInsertElement.style.setProperty('display', 'flex', 'important');
                    quickInsertElement.style.setProperty('visibility', 'visible', 'important');
                    quickInsertElement.style.setProperty('opacity', '1', 'important');
                  }
                }
              } catch (e) {
                // Ignore
              }
            });
          } catch (e) {
            if (editor.$el) {
              editor.$el.focus();
            } else if (textArea) {
              textArea.focus();
            }
          }
          return true;
        };
        
        if (!editorWrapper._checkingEditor) {
          editorWrapper._checkingEditor = true;
        } else {
          return;
        }
        
        const checkForEditorWithCallback = (attempt = 1, maxAttempts = 2) => {
          let editor = null;
          if (textArea) {
            editor = textArea.froalaEditor;
            
            if (!editor && typeof jQuery !== 'undefined') {
              editor = jQuery(textArea).data('froala.editor');
            }
            
            if (!editor) {
              const frBox = editorWrapper.querySelector('.fr-box');
              if (frBox) {
                editor = frBox.froalaEditor;
                if (!editor && typeof jQuery !== 'undefined') {
                  editor = jQuery(frBox).data('froala.editor');
                }
                
                if (!editor && frBox.parentElement) {
                  editor = frBox.parentElement.froalaEditor;
                }
                
                if (!editor && textArea.nextElementSibling) {
                  editor = textArea.nextElementSibling.froalaEditor;
                }
                
                if (!editor) {
                  const frElement = editorWrapper.querySelector('.fr-element');
                  if (frElement && frElement !== textArea) {
                    editor = frElement.froalaEditor;
                    if (!editor && typeof jQuery !== 'undefined') {
                      editor = jQuery(frElement).data('froala.editor');
                    }
                    if (!editor && frElement.parentElement) {
                      editor = frElement.parentElement.froalaEditor;
                    }
                  }
                }
                
                if (!editor && typeof FroalaEditor !== 'undefined') {
                  if (FroalaEditor.get) {
                    try {
                      editor = FroalaEditor.get(textArea);
                    } catch (e) {
                      // Ignore errors
                    }
                  }
                  if (!editor && FroalaEditor.instances && FroalaEditor.instances[textArea]) {
                    editor = FroalaEditor.instances[textArea];
                  }
                }
                
                if (!editor && attempt >= 5 && attempt < 8) {
                  const frElement = editorWrapper.querySelector('.fr-element');
                  if (frElement && frElement !== textArea) {
                    try {
                      if (!frElement._focusAttempted) {
                        frElement._focusAttempted = true;
                        frElement.focus();
                      }
                    } catch (e) {
                      // Ignore errors
                    }
                  }
                }
                
                if (editor) {
                  textArea.froalaEditor = editor;
                  if (typeof jQuery !== 'undefined') {
                    jQuery(textArea).data('froala.editor', editor);
                  }
                }
              }
            }
          }
          
          if (editor) {
            editorWrapper._checkingEditor = false;
            handleEditorFound(editor);
          } else if (attempt < maxAttempts) {
            requestAnimationFrame(() => checkForEditorWithCallback(attempt + 1, maxAttempts));
          } else {
            editorWrapper._checkingEditor = false;
            
            if (textArea && textArea.dataset.froalaInitialized === "true") {
              const frElement = editorWrapper.querySelector('.fr-element');
              if (frElement && frElement !== textArea) {
                try {
                  frElement.focus();
                  return;
                } catch (e) {
                  // Ignore errors
                }
              }
            }
            
            const frBox = editorWrapper.querySelector('.fr-box');
            const frElement = editorWrapper.querySelector('.fr-element');
            if (frBox || frElement) {
              if (frElement && frElement !== textArea) {
                try {
                  frElement.focus();
                } catch (e) {
                  // Ignore errors
                }
              }
              return;
            }
            
            editorWrapper._checkingEditor = false;
            this.fetchMergeTags(editorWrapper)
              .then((mergeTags) => {
                this.initializeFroala(editorWrapper, mergeTags);
                setTimeout(() => {
                  const textArea = editorWrapper.querySelector(".froala");
                  if (textArea && textArea.froalaEditor) {
                    const contentDivHtml = contentDiv.innerHTML;
                    if (contentDivHtml && contentDivHtml.trim() !== '' && 
                        !contentDivHtml.includes('no-content') &&
                        (contentDivHtml.includes('<') || contentDivHtml.includes('&'))) {
                      const cleanHtml = this.formatContentForEditor(contentDivHtml);
                      if (cleanHtml) {
                        textArea.froalaEditor.html.set(cleanHtml);
                      }
                    }
                    textArea.froalaEditor.events.focus();
                    textArea.froalaEditor.selection.setAtEnd();
                    textArea.froalaEditor.selection.restore();
                  }
                }, 100);
              })
              .catch(error => {
                console.error("Failed to fetch merge tags:", error);
                this.initializeFroala(editorWrapper, []);
              });
          }
        };
        
        checkForEditorWithCallback();
      }
      event.stopPropagation();
    }
  }

  hideAllOtherEditors(currentContentDiv) {
    const allEditorWrappers = document.querySelectorAll(".froala-editor-wrapper");
    allEditorWrappers.forEach(editorWrapper => {
      const contentDiv = editorWrapper.previousElementSibling;
      if (contentDiv && contentDiv !== currentContentDiv && !editorWrapper.classList.contains("hidden")) {
        editorWrapper.classList.add("hidden");
        editorWrapper.style.display = "none";
        if (contentDiv) {
          contentDiv.style.display = "";
        }
      }
    });
  }



  hideEditor(editorWrapper) {
    console.log("Hide Editor Event Is Triggered");
    if (this.isReadOnly) return;

    // Stop editing when hiding editor
    window.activeEditingManager.stopEditing(this.blockId, 500);

    const contentDiv = editorWrapper.previousElementSibling;
    if (contentDiv) {
      const textArea = editorWrapper.querySelector(".froala");
      
      // Get the current content from the editor BEFORE saving to prevent reversion
      let currentContent = '';
      if (textArea && textArea.froalaEditor) {
        currentContent = textArea.froalaEditor.html.get();
      } else if (textArea) {
        currentContent = textArea.value || '';
      }
      
      // Clean up action-text-attachment tags
      currentContent = currentContent.replace(/<action-text-attachment/g, '<img')
                                    .replace(/<\/action-text-attachment>/g, '')
                                    .replace(/url=/g, 'src=');
      
      // Update content div with the current editor content IMMEDIATELY
      if (currentContent && currentContent.trim() !== '') {
        contentDiv.innerHTML = currentContent;
        setTimeout(() => {
          const styledElements = contentDiv.querySelectorAll('[style*="font-size"], [style*="color"]');
          console.log('🔍 Found styled elements after setting innerHTML:', styledElements.length);
          styledElements.forEach((el, idx) => {
            console.log(`🔍 Element ${idx} style:`, el.getAttribute('style'));
            console.log(`🔍 Element ${idx} computed font-size:`, window.getComputedStyle(el).fontSize);
            console.log(`🔍 Element ${idx} computed color:`, window.getComputedStyle(el).color);
          });
        }, 100);
      } else {
        contentDiv.innerHTML = '<span class="no-content">Edit...</span>';
      }
      
      // Save content to backend (async, but content div is already updated)
      this.saveContent(textArea);
      
      editorWrapper.classList.add("hidden");
      editorWrapper.style.display = "none";
      editorWrapper.style.visibility = "";
      editorWrapper.style.opacity = "";
      
      contentDiv.style.display = "";
      contentDiv.style.visibility = "";
      contentDiv.style.opacity = "";
    }
  }

  setupTaskBlockUpdateListener() {
    document.addEventListener('taskBlockUpdated', (event) => {
      console.log('🔧 taskBlockUpdated event received:', event.detail);
      const { block_id, account_id, project_id, task_list_id, task_id, skip_self_update, source_block_id } = event.detail;
      
      // Check if this update affects our block
      const container = this.element.closest('.rich-text-container');
      if (container) {
        const containerBlockId = container.dataset.blockId;
        const containerAccountId = container.dataset.accountId;
        const containerProjectId = container.dataset.projectId;
        const containerTaskListId = container.dataset.taskListId;
        const containerTaskId = container.dataset.taskId;
        
        console.log('🔧 Container data:', { containerBlockId, containerAccountId, containerProjectId, containerTaskListId, containerTaskId });
        console.log('🔧 Event data:', { block_id, account_id, project_id, task_list_id, task_id });
        
        if (skip_self_update && source_block_id && source_block_id == containerBlockId) {
          return;
        }
        
        if (account_id == containerAccountId && 
            project_id == containerProjectId && 
            task_list_id == containerTaskListId && 
            task_id == containerTaskId &&
            block_id == containerBlockId) {
          
          console.log('🔧 Task match found! Block update detected. Scheduling content update...');
          
          // Only update if editor is not focused
          const textArea = this.element.querySelector('.froala');
          const editorWrapper = this.element.querySelector('.froala-editor-wrapper');
          
          // Check if editor is currently visible (not hidden)
          if (textArea && editorWrapper && editorWrapper.classList.contains('hidden')) {
            console.log('🔧 Editor is in display mode, scheduling content update...');
            this.scheduleContentUpdate();
          } else {
            console.log('🔧 Editor is in edit mode, skipping update...');
          }
        } else {
          console.log('🔧 No match found, skipping update...');
        }
      } else {
        console.log('🔧 No container found');
      }
    });
  }

  scheduleContentUpdate() {
    clearTimeout(this.timeout);

    this.timeout = setTimeout(() => {
      this.performContentUpdate();
    }, 100);
  }

  scheduleEditorHide(editorWrapper) {
    clearTimeout(this.hideTimeout);

    this.hideTimeout = setTimeout(() => {
      if (this.isMouseDown) {
        return;
      }
      
      const textArea = editorWrapper.querySelector(".froala");
      if (textArea && textArea.froalaEditor) {
        try {
          const selection = textArea.froalaEditor.selection.get();
          if (selection && !selection.collapsed) {
            return;
          }
        } catch (e) {
          
        }
      }
      
      this.hideEditor(editorWrapper);
    }, 100);
  }

  // Format content for editor - preserves inline styles and HTML structure
  formatContentForEditor(htmlContent) {
    if (!htmlContent) return '';
    
    // Create a temporary div to safely process HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    
    // Remove any script tags for security
    const scripts = tempDiv.querySelectorAll('script');
    scripts.forEach(script => script.remove());
    
    // Remove any style tags (but preserve inline styles on elements)
    const styles = tempDiv.querySelectorAll('style');
    styles.forEach(style => style.remove());
    
    // Clean up common issues while preserving styling
    let cleanHtml = tempDiv.innerHTML
      .replace(/<p><br><\/p>/g, '') // Remove empty paragraphs
      .replace(/<p><\/p>/g, '') // Remove empty paragraphs
      .trim();
    
    // If content is empty after cleaning, return empty string
    if (!cleanHtml || cleanHtml === '') {
      return '';
    }
    
    return cleanHtml;
  }

  performContentUpdate() {
    if (this.isUpdatingContent) {
      console.log("Content update already in progress, skipping...");
      return;
    }

    this.isUpdatingContent = true;

    const container = this.element.closest('.rich-text-container');
    if (!container) {
      console.log('❌ No container found');
      this.isUpdatingContent = false;
      return;
    }
    
    const { taskId, accountId, projectId, taskListId, blockId, contentType } = container.dataset;
    console.log('🔧 Container data:', { taskId, accountId, projectId, taskListId, blockId, contentType });
    
    const url = `/${accountId}/projects/${projectId}/task_lists/${taskListId}/tasks/${taskId}/task_blocks/${blockId}/process_liquid_content?content_type=${contentType}`;
    console.log('🔧 Fetching URL:', url);
    
    fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
      }
    })
    .then(response => {
      console.log('🔧 Response status:', response.status);
      return response.json();
    })
    .then(data => {
      console.log('🔧 Response data:', data);
      if (data.processed_content) {
        const contentDiv = this.element.querySelector('.rich-text-content');
        if (contentDiv) {
          console.log('🔧 Updating content div with:', data.processed_content);
          contentDiv.innerHTML = data.processed_content;
        } else {
          console.log('❌ No content div found');
        }
      } else {
        console.log('❌ No processed_content in response');
      }
    })
    .catch(error => {
      console.error("Error updating content display:", error);
    })
    .finally(() => {
      this.isUpdatingContent = false;
    });
  }


  disconnect() {
    this.isConnected = false;
    
    // Clean up editing state when controller disconnects
    if (this.blockId) {
      window.activeEditingManager.stopEditing(this.blockId, 0); // No debounce on disconnect
    }
    
    if (this.autoSaveTimeout) {
      clearTimeout(this.autoSaveTimeout);
    }
    if (this.contentChangeTimeout) {
      clearTimeout(this.contentChangeTimeout);
    }
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }
    
    // Clean up all initialized editors
    if (this.editorWrappers) {
      this.editorWrappers.forEach(editorWrapper => {
        const textArea = editorWrapper.querySelector(".froala");
        if (textArea && textArea.froalaEditor) {
          textArea.froalaEditor.destroy();
          console.log('🧹 Editor destroyed on disconnect');
        }
      });
    }
  }

  initializeFroala(editorWrapper, liquidTags) {
    setTimeout(() => {
      console.log("editorWrapper ====>", editorWrapper);
      const textArea = editorWrapper.querySelector(".froala");
      
      // Add dark theme CSS for dropdown
      const darkThemeCSS = `
        .fr-dropdown-dark,
        .fr-dropdown-dark *,
        .fr-dropdown-dark .fr-dropdown-item,
        .fr-dropdown-dark .fr-dropdown-item * {
          background-color: #2d3748 !important;
          color: #ffffff !important;
          border-color: #4a5568 !important;
        }
        
        .fr-dropdown-dark {
          border: 1px solid #4a5568 !important;
          border-radius: 4px !important;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3) !important;
        }
        
        .fr-dropdown-dark .fr-dropdown-item {
          border-bottom: 1px solid #4a5568 !important;
          padding: 8px 12px !important;
        }
        
        .fr-dropdown-dark .fr-dropdown-item:hover {
          background-color: #4a5568 !important;
          color: #ffffff !important;
        }
        
        .fr-dropdown-dark .fr-dropdown-item:last-child {
          border-bottom: none !important;
        }
        
        /* Override any existing styles */
        .fr-dropdown-dark .fr-dropdown-item a,
        .fr-dropdown-dark .fr-dropdown-item span,
        .fr-dropdown-dark .fr-dropdown-item div {
          background-color: #2d3748 !important;
          color: #ffffff !important;
        }
        
        .fr-dropdown-dark .fr-dropdown-item:hover a,
        .fr-dropdown-dark .fr-dropdown-item:hover span,
        .fr-dropdown-dark .fr-dropdown-item:hover div {
          background-color: #4a5568 !important;
          color: #ffffff !important;
        }
      `;

      // Inject CSS if not already present
      if (!document.querySelector('#froala-dark-dropdown-css')) {
        const style = document.createElement('style');
        style.id = 'froala-dark-dropdown-css';
        style.textContent = darkThemeCSS;
        document.head.appendChild(style);
      }
      if (textArea) {
          let htmlContent = textArea.value.trim();
          htmlContent = htmlContent.replace(/<action-text-attachment/g, '<img')
                                  .replace(/<\/action-text-attachment>/g, '')
                                  .replace(/url=/g, 'src=');
          textArea.value = htmlContent;
      }
      if (!textArea) {
        console.error("🚨 Froala editor target not found inside editorWrapper!", editorWrapper);
        return;
      }
  
      // Clean up and fix broken action-text-attachment blocks
      let htmlContent = textArea.value.trim();
      htmlContent = htmlContent.replace(/<action-text-attachment/g, '<img')
                                .replace(/<\/action-text-attachment>/g, '')
                                .replace(/url=/g, 'src=');
      textArea.value = htmlContent;
  
      
      let existingEditor = null;
      if (textArea.froalaEditor) {
        existingEditor = textArea.froalaEditor;
      } else if (typeof jQuery !== 'undefined' && jQuery(textArea).data('froala.editor')) {
        existingEditor = jQuery(textArea).data('froala.editor');
      }
      
      if (textArea.dataset.froalaInitialized === "true" && existingEditor) {
        return;
      }
      
      
      if (existingEditor) {
        try {
          existingEditor.destroy();
        } catch (e) {
          console.warn("⚠️ Error destroying existing editor:", e);
        }
      }
      
      
      if (typeof jQuery !== 'undefined' && jQuery(textArea).data('froala.editor')) {
        try {
          const jqEditor = jQuery(textArea).data('froala.editor');
          if (jqEditor && jqEditor.destroy) {
            jqEditor.destroy();
          }
          jQuery(textArea).removeData('froala.editor');
        } catch (e) {
          console.warn("⚠️ Error destroying editor via jQuery:", e);
        }
      }
      
      
      if (textArea.dataset.froalaInitialized === "true") {
        textArea.dataset.froalaInitialized = "false";
      }
      
      
      const frBox = editorWrapper.querySelector('.fr-box');
      const frElement = editorWrapper.querySelector('.fr-element');
      if (frBox || frElement) {
        let existingEditorInstance = null;
        
        
        if (textArea.froalaEditor) {
          existingEditorInstance = textArea.froalaEditor;
        }
        
        
        if (!existingEditorInstance && typeof jQuery !== 'undefined') {
          existingEditorInstance = jQuery(textArea).data('froala.editor');
        }
        
        
        if (!existingEditorInstance && frBox) {
          if (frBox.froalaEditor) {
            existingEditorInstance = frBox.froalaEditor;
          } else if (typeof jQuery !== 'undefined') {
            existingEditorInstance = jQuery(frBox).data('froala.editor');
          }
        }
        
        
        if (!existingEditorInstance && frElement && frElement !== textArea) {
          if (frElement.froalaEditor) {
            existingEditorInstance = frElement.froalaEditor;
          } else if (typeof jQuery !== 'undefined') {
            existingEditorInstance = jQuery(frElement).data('froala.editor');
          }
        }
        
        
        if (!existingEditorInstance && typeof FroalaEditor !== 'undefined' && FroalaEditor.get) {
          try {
            existingEditorInstance = FroalaEditor.get(textArea);
          } catch (e) {
          }
        }
        
        if (existingEditorInstance) {
          textArea.froalaEditor = existingEditorInstance;
          
          if (typeof jQuery !== 'undefined') {
            jQuery(textArea).data('froala.editor', existingEditorInstance);
          }
          
          textArea.dataset.froalaInitialized = "true";
          return; 
        } else {
          return;
        }
      }
      
      // Define dropdown command name and options
      let dropdownCommandName = null;
      let dropdownOptions = {};
      
      if (liquidTags && liquidTags.length > 0) {
        dropdownCommandName = `liquidTagDropdown_${Date.now()}`;
        dropdownOptions = liquidTags.reduce((options, tag, index) => {
          options[index] = tag.title;
          return options;
        }, {});
        
        console.log('🔧 Created dropdown command:', dropdownCommandName);
        console.log('🔧 Dropdown options:', dropdownOptions);
      } else {
        console.log('🔧 No liquid tags available, skipping dropdown creation');
      }

      if (dropdownCommandName) {
        FroalaEditor.DefineIcon(dropdownCommandName, { NAME: "code", SVG_KEY: "tags" });
        FroalaEditor.RegisterCommand(dropdownCommandName, {
          title: "Insert Liquid Tag",
          type: "dropdown",
          focus: true,
          options: dropdownOptions,
                                          callback: function (cmd, value) {
            const index = parseInt(value, 10);
            const selectedTag = liquidTags[index]?.value;
            
            // Insert tag at current cursor position instead of appending to end
            try {
              // Restore the current selection
              this.selection.restore();
              
              // Get the current selection range
              const selection = this.selection.get();
              if (selection && selection.range) {
                // Insert the tag at the current cursor position
                const range = selection.range;
                const textNode = document.createTextNode(selectedTag);
                range.insertNode(textNode);
                
                // Move cursor to the end of the inserted text
                range.setStartAfter(textNode);
                range.setEndAfter(textNode);
                this.selection.set(range);
              } else {
                // Fallback: use Froala's insert method which respects cursor position
                this.html.insert(selectedTag);
              }
              
              // Focus the editor to ensure cursor is visible and maintain focus
              setTimeout(() => {
                this.events.focus();
                this.selection.restore();
              }, 10);
            } catch (error) {
              console.error('Error inserting liquid tag:', error);
              // Fallback: use the HTML insert method
              this.html.insert(selectedTag);
            }
            
            // Mark operation as complete
            if (window.currentFroalaController) {
              window.currentFroalaController.markOperationComplete();
            }
          },
          // Dark theme styling for dropdown
          popup: {
            className: 'fr-dropdown-dark',
            style: {
              backgroundColor: '#2d3748',
              border: '1px solid #4a5568',
              borderRadius: '4px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }
          }
        });


        console.log('🔧 Registering quick insert button:', dropdownCommandName);
        FroalaEditor.RegisterQuickInsertButton(dropdownCommandName, {
        title: "Insert Liquid Tag",
        type: "quickInsert",
        icon: dropdownCommandName,
        focus: true,
        callback: function (event) {
          console.log('🔧 Liquid tag button clicked - showing custom dropdown');

          // Handle event if it exists and has the required methods
          if (event && typeof event.preventDefault === 'function') {
            event.preventDefault();
          }
          if (event && typeof event.stopPropagation === 'function') {
            event.stopPropagation();
          }

          // Capture the mouse position from the event (if available)
          let mouseX = null;
          let mouseY = null;
          if (event) {
            mouseX = event.clientX || event.pageX;
            mouseY = event.clientY || event.pageY;
            console.log('🔧 Mouse position:', { mouseX, mouseY });
          }

          // Ensure editor is focused and cursor is visible
          try {
            this.events.focus();
          } catch (error) {
            console.log('🔧 Could not focus editor:', error);
          }

          // Check if editor is properly initialized
          if (!this.el || !this.selection) {
            console.error('❌ Editor not properly initialized');
            return;
          }

          // Small delay to ensure focus is established
          setTimeout(() => {

          // Remove existing dropdown if any
          const existingDropdown = document.querySelector('.liquid-tag-quick-insert-dropdown');
          if (existingDropdown) {
            existingDropdown.remove();
          }

          // Create custom dropdown
          const dropdown = document.createElement('div');
          dropdown.className = 'liquid-tag-quick-insert-dropdown';

          // Add dark theme class if editor is in dark mode
          if (this.opts.theme === 'dark') {
            dropdown.classList.add('dark-theme');
          }

          dropdown.style.cssText = `
            position: fixed;
            background: white;
            border: 1px solid #ccc;
            border-radius: 4px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
            z-index: 9999;
            max-height: 200px;
            overflow-y: auto;
            min-width: 200px;
            display: none;
          `;

          // Add header
          const header = document.createElement('div');
          header.style.cssText = `
            padding: 8px 12px;
            background: #f8f9fa;
            border-bottom: 1px solid #dee2e6;
            font-weight: bold;
            font-size: 12px;
            color: #495057;
          `;
          header.textContent = 'Select Liquid Tag';
          dropdown.appendChild(header);

          // Add options
          liquidTags.forEach((tag, index) => {
            const option = document.createElement('div');
            option.className = 'liquid-tag-option';
            option.style.cssText = `
              padding: 8px 12px;
              cursor: pointer;
              border-bottom: 1px solid #f1f3f4;
              font-size: 12px;
              color: #495057;
              transition: background-color 0.2s;
            `;
            option.textContent = tag.title;

            // Hover effect
            option.addEventListener('mouseenter', () => {
              // Clear previous selection
              options.forEach((opt, idx) => {
                if (idx !== index) {
                  opt.style.backgroundColor = 'transparent';
                }
              });
              option.style.backgroundColor = '#e9ecef';
              selectedIndex = index;
            });
            option.addEventListener('mouseleave', () => {
              // Don't clear background on mouseleave to maintain keyboard selection
            });

                         // Click handler
             option.addEventListener('click', () => {
               // Insert tag at current cursor position instead of appending to end
               try {
                 // Restore the current selection
                 this.selection.restore();
                 
                 // Get the current selection range
                 const selection = this.selection.get();
                 if (selection && selection.range) {
                   // Insert the tag at the current cursor position
                   const range = selection.range;
                   const textNode = document.createTextNode(tag.value);
                   range.insertNode(textNode);
                   
                   // Move cursor to the end of the inserted text
                   range.setStartAfter(textNode);
                   range.setEndAfter(textNode);
                   this.selection.set(range);
                 } else {
                   // Fallback: use Froala's insert method which respects cursor position
                   this.html.insert(tag.value);
                 }
                 
                 // Focus the editor to ensure cursor is visible and maintain focus
                 setTimeout(() => {
                   this.events.focus();
                   this.selection.restore();
                 }, 10);
               } catch (error) {
                 console.error('Error inserting liquid tag:', error);
                 // Fallback: use the HTML insert method
                 this.html.insert(tag.value);
               }

               dropdown.remove();
               // Hide quick insert toolbar after selection
              if (this.quickInsert) {
                // Allow hiding after dropdown selection
                this.quickInsert._keepVisible = false;
                this.quickInsert._allowHide = true;
                if (this.quickInsert._originalHide) {
                  this.quickInsert._originalHide();
                }
               }
               
               // Mark operation as complete
               if (window.currentFroalaController) {
                 window.currentFroalaController.markOperationComplete();
               }
             });

            dropdown.appendChild(option);
          });

          // Position dropdown at cursor position
          const positionDropdown = () => {
            console.log('🔧 Positioning dropdown...');

            // Get the editor element
            const editorElement = this.el;
            if (!editorElement) {
              console.error('❌ Editor element not found');
              return;
            }

            // Get the editor's bounding rect
            const editorRect = editorElement.getBoundingClientRect();
            console.log('🔧 Editor rect:', editorRect);

            // Try to get the current cursor position
            let cursorPosition = null;

            // First, try to use the mouse position from the click event
            if (mouseX && mouseY) {
              cursorPosition = {
                left: mouseX,
                top: mouseY,
                width: 1,
                height: 1
              };
              console.log('🔧 Using mouse position as cursor position:', cursorPosition);
            }

            try {
              // Method 1: Get cursor position from current selection
              const selection = window.getSelection();
              if (selection && selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                const rect = range.getBoundingClientRect();
                if (rect && rect.width > 0 && rect.height > 0) {
                  cursorPosition = {
                    left: rect.left,
                    top: rect.bottom,
                    width: rect.width,
                    height: rect.height
                  };
                  console.log('🔧 Got cursor position from selection:', cursorPosition);
                }
              }

              // Method 2: Try to get position from the currently focused element
              if (!cursorPosition) {
                const focusedElement = document.activeElement;
                if (focusedElement && focusedElement !== document.body) {
                  const focusedRect = focusedElement.getBoundingClientRect();
                  if (focusedRect && focusedRect.width > 0 && focusedRect.height > 0) {
                    cursorPosition = {
                      left: focusedRect.left,
                      top: focusedRect.bottom + 5,
                      width: focusedRect.width,
                      height: 1
                    };
                    console.log('🔧 Got cursor position from focused element:', cursorPosition);
                  }
                }
              }

              // Method 3: If no selection, try to get position from editor's content
              if (!cursorPosition) {
                // Find the last text node in the editor
                const textNodes = [];
                const walker = document.createTreeWalker(
                  editorElement,
                  NodeFilter.SHOW_TEXT,
                  null,
                  false
                );

                let node;
                while (node = walker.nextNode()) {
                  if (node.textContent.trim()) {
                    textNodes.push(node);
                  }
                }

                if (textNodes.length > 0) {
                  const lastTextNode = textNodes[textNodes.length - 1];
                  const range = document.createRange();
                  range.selectNodeContents(lastTextNode);
                  range.collapse(false); // Collapse to end

                  const rect = range.getBoundingClientRect();
                  if (rect && rect.width > 0 && rect.height > 0) {
                    cursorPosition = {
                      left: rect.left + rect.width,
                      top: rect.bottom,
                      width: 1,
                      height: 1
                    };
                    console.log('🔧 Got cursor position from last text node:', cursorPosition);
                  }
                }
              }

              // Method 4: Try to get position from the button that was clicked
              if (!cursorPosition) {
                const button = document.querySelector(`[data-cmd="${dropdownCommandName}"]`);
                if (button) {
                  const buttonRect = button.getBoundingClientRect();
                  cursorPosition = {
                    left: buttonRect.left,
                    top: buttonRect.bottom + 5,
                    width: buttonRect.width,
                    height: 1
                  };
                  console.log('🔧 Using button position as cursor position:', cursorPosition);
                }
              }

              // Method 5: If still no position, use the editor's center
              if (!cursorPosition) {
                cursorPosition = {
                  left: editorRect.left + (editorRect.width / 2),
                  top: editorRect.top + (editorRect.height / 2),
                  width: 1,
                  height: 1
                };
                console.log('🔧 Using editor center as cursor position:', cursorPosition);
              }

            } catch (error) {
              console.error('❌ Error getting cursor position:', error);
              // Fallback to editor center
              cursorPosition = {
                left: editorRect.left + (editorRect.width / 2),
                top: editorRect.top + (editorRect.height / 2),
                width: 1,
                height: 1
              };
            }

            // Position the dropdown relative to the cursor
            if (cursorPosition) {
              let leftPosition = cursorPosition.left;
              let topPosition = cursorPosition.top + 5; // 5px below cursor

              // Get dropdown dimensions
              const dropdownHeight = dropdown.offsetHeight || 200;
              const dropdownWidth = dropdown.offsetWidth || 200;

              // Check window boundaries
              const windowHeight = window.innerHeight;
              const windowWidth = window.innerWidth;

              // Adjust vertical position if needed
              if (topPosition + dropdownHeight > windowHeight) {
                // Not enough space below, position above cursor
                topPosition = cursorPosition.top - dropdownHeight - 5;

                // If still not enough space, position at top of viewport
                if (topPosition < 10) {
                  topPosition = 10;
                }
              }

              // Adjust horizontal position if needed
              if (leftPosition + dropdownWidth > windowWidth) {
                // Not enough space to the right, position to the left
                leftPosition = Math.max(10, leftPosition - dropdownWidth);
              }

              // Ensure minimum positions
              leftPosition = Math.max(10, leftPosition);
              topPosition = Math.max(10, topPosition);

              // Apply positioning
              dropdown.style.left = leftPosition + 'px';
              dropdown.style.top = topPosition + 'px';
              dropdown.style.display = 'block';
            } else {
              // Fallback positioning if no cursor position found
              dropdown.style.left = '50px';
              dropdown.style.top = '50px';
              dropdown.style.display = 'block';
            }
          };

          // Add to document
          document.body.appendChild(dropdown);

          // Position dropdown first, then show it
          console.log('🔧 Positioning dropdown...');
          positionDropdown();

          // Add keyboard navigation support
          let selectedIndex = 0;
          const options = dropdown.querySelectorAll('.liquid-tag-option');

          // Add cursor tracking for dynamic positioning
          const updateDropdownPosition = () => {
            if (!dropdown || dropdown.style.display === 'none') return;

            // Get current cursor position
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0) {
              const range = selection.getRangeAt(0);
              const rect = range.getBoundingClientRect();

              if (rect && rect.width > 0 && rect.height > 0) {
                let leftPosition = rect.left;
                let topPosition = rect.bottom + 5;

                // Get dropdown dimensions
                const dropdownHeight = dropdown.offsetHeight || 200;
                const dropdownWidth = dropdown.offsetWidth || 200;

                // Check window boundaries
                const windowHeight = window.innerHeight;
                const windowWidth = window.innerWidth;

                // Adjust vertical position if needed
                if (topPosition + dropdownHeight > windowHeight) {
                  topPosition = rect.top - dropdownHeight - 5;
                  if (topPosition < 10) {
                    topPosition = 10;
                  }
                }

                // Adjust horizontal position if needed
                if (leftPosition + dropdownWidth > windowWidth) {
                  leftPosition = Math.max(10, leftPosition - dropdownWidth);
                }

                // Ensure minimum positions
                leftPosition = Math.max(10, leftPosition);
                topPosition = Math.max(10, topPosition);

                // Apply new position
                dropdown.style.left = leftPosition + 'px';
                dropdown.style.top = topPosition + 'px';
              }
            }
          };

          // Track cursor movement
          const trackCursor = () => {
            // Update position on mouse move
            document.addEventListener('mousemove', updateDropdownPosition);

            // Update position on key navigation
            document.addEventListener('keydown', updateDropdownPosition);

            // Update position on scroll
            document.addEventListener('scroll', updateDropdownPosition);
          };

          // Start tracking cursor
          trackCursor();

          const handleKeyDown = (e) => {
            if (!dropdown || dropdown.style.display === 'none') return;

            switch (e.key) {
              case 'ArrowDown':
                e.preventDefault();
                e.stopPropagation();
                if (selectedIndex < options.length - 1) {
                  options[selectedIndex].style.backgroundColor = 'transparent';
                  selectedIndex++;
                  options[selectedIndex].style.backgroundColor = '#e9ecef';
                  options[selectedIndex].scrollIntoView({ block: 'nearest' });
                }
                break;

              case 'ArrowUp':
                e.preventDefault();
                e.stopPropagation();
                if (selectedIndex > 0) {
                  options[selectedIndex].style.backgroundColor = 'transparent';
                  selectedIndex--;
                  options[selectedIndex].style.backgroundColor = '#e9ecef';
                  options[selectedIndex].scrollIntoView({ block: 'nearest' });
                }
                break;

              case 'Enter':
                e.preventDefault();
                e.stopPropagation();
                if (options[selectedIndex]) {
                  options[selectedIndex].click();
                }
                break;

              case 'Escape':
                e.preventDefault();
                e.stopPropagation();
                dropdown.remove();
                document.removeEventListener('keydown', handleKeyDown);
                document.removeEventListener('click', closeDropdown);
                document.removeEventListener('mousemove', updateDropdownPosition);
                document.removeEventListener('keydown', updateDropdownPosition);
                document.removeEventListener('scroll', updateDropdownPosition);
                break;
            }
          };

          // Close dropdown when clicking outside
          const closeDropdown = (e) => {
            if (!dropdown.contains(e.target) && !e.target.closest(`[data-cmd="${dropdownCommandName}"]`)) {
              dropdown.remove();
              document.removeEventListener('click', closeDropdown);
              document.removeEventListener('keydown', handleKeyDown);
              document.removeEventListener('mousemove', updateDropdownPosition);
              document.removeEventListener('keydown', updateDropdownPosition);
              document.removeEventListener('scroll', updateDropdownPosition);
            }
          };

          // Add event listeners
          setTimeout(() => {
            document.addEventListener('click', closeDropdown);
            document.addEventListener('keydown', handleKeyDown);

            // Set initial selection
            if (options.length > 0) {
              options[0].style.backgroundColor = '#e9ecef';
            }
          }, 100);
        }, 50); // Close the setTimeout from the callback
        }
      });

      console.log('🔧 Quick insert button registered successfully:', dropdownCommandName);
      this.dropdownCommandName = dropdownCommandName;
      } // Close the if (dropdownCommandName) block

      const tableBorderCommandName = 'tableBorderFormat';
      // Define icon using PATH with SVG path data for a hollow grid/table with borders
      // Rectangle outline (no fill) + grid lines
      FroalaEditor.DefineIcon(tableBorderCommandName, {
        PATH: 'M3 3h18v18h-18z M3 9h18 M3 15h18 M9 3v18 M15 3v18',
        fill: 'none'
      });
      
      FroalaEditor.RegisterCommand(tableBorderCommandName, {
        title: "Table Border Width",
        type: "dropdown",
        focus: false,
        undo: true,
        options: {
          '0': 'No Border (0px)',
          '0.5': 'Thin (0.5px)',
          '1': '1px',
          '2': '2px',
          '3': '3px',
          '4': '4px',
          '5': 'Thick (5px)'
        },
        callback: function (cmd, value) {
          console.log('🔍 Table border callback called with value:', value);
          const borderWidth = value === '0' ? '0' : value + 'px';
          const borderColor = '#ddd'; // Default border color
          const editor = this;
          let table = null;
          
          const selectedElement = editor.selection.element();
          console.log('🔍 Selected element:', selectedElement);
          
          if (selectedElement) {
            if (selectedElement.tagName === 'TABLE') {
              table = selectedElement;
            } else {
              table = selectedElement.closest('table');
            }
          }
          
          if (!table) {
            const selection = editor.selection.get();
            if (selection && selection.range) {
              let node = selection.range.commonAncestorContainer;
              if (node.nodeType !== 1) {
                node = node.parentElement;
              }
              if (node) {
                table = node.closest('table');
              }
            }
          }
          
          if (!table) {
            const editorElement = editor.$el[0] || editor.el;
            if (editorElement) {
              table = editorElement.querySelector('table');
            }
          }
          
          console.log('🔍 Found table:', table);
          
          if (!table || table.tagName !== 'TABLE') {
            console.error('❌ No table found to apply borders');
            return;
          }
          
          const cells = table.querySelectorAll('td, th');
          console.log('🔍 Applying borders to', cells.length, 'cells');
          
          cells.forEach(function(cell) {
            if (borderWidth === '0') {
              cell.style.border = 'none';
              cell.style.borderWidth = '0';
              cell.style.borderStyle = 'none';
              cell.removeAttribute('border');
            } else {
              cell.style.border = borderWidth + ' solid ' + borderColor;
              cell.style.borderWidth = borderWidth;
              cell.style.borderStyle = 'solid';
              cell.style.borderColor = borderColor;
            }
          });
          
          if (borderWidth === '0') {
            table.style.border = 'none';
            table.style.borderWidth = '0';
            table.style.borderStyle = 'none';
            table.removeAttribute('border');
          } else {
            table.style.border = borderWidth + ' solid ' + borderColor;
            table.style.borderWidth = borderWidth;
            table.style.borderStyle = 'solid';
            table.style.borderColor = borderColor;
          }
          
          table.style.borderCollapse = 'collapse';
          table.style.borderSpacing = '0';
          
          const currentHtml = editor.html.get();
          editor.html.set(currentHtml);
          
          editor.events.trigger('contentChanged', [], true);
        }
      });

      // Web Toolbar
      const webToolbarButtons = [dropdownCommandName].filter(Boolean).concat([
        'bold', 'italic', 'fontSize', 'textColor', 'underline', 'undo', 'redo', 'clearFormatting', 'fullscreen', 'insertTable', tableBorderCommandName, 'fontFamily', 'backgroundColor', 'align', 'formatOL', 'formatUL', 'lineHeight', 'outdent', 'indent', 'quote', 'insertImage', 'insertVideo', 'embedly', 'insertLink', 'insertHR', 'print', 'selectAll'
      ]);
      
      const webToolbar = {
        moreMisc:{
          buttons: webToolbarButtons,
          buttonsVisible : 8
        }
      };

      const froalaApiKey = document.querySelector('meta[name="froala-api-key"]')?.content;
      new FroalaEditor(textArea, {
        key: froalaApiKey,
        theme: 'dark',
        dragInline: false,
        attribution: false,
        toolbarSticky: false,
        toolbarInline: true,
        toolbarVisibleWithoutSelection: false,
        charCounterCount: false,
        wordCounterCount: false,
        height: 120,
        heightMin: 120,
        heightMax: null,
        imageUpload: true,
        fileUpload: true,
        imageUploadURL: '/froala_file_uploader',
        fileUploadURL: '/froala_file_uploader',
        imageResize: true,
        imageUploadParams: {
          authenticity_token: document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        },
        fileUploadParams: {
          authenticity_token: document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        },
        imageEditButtons: ['imageStyle', 'imageAlign', 'imageDisplay', 'imageAlt', 'imageSize', 'imageLink', 'imageRemove'],
        imageResizeWithPercent: true,
        
        tableEditButtons: ['tableHeader', 'tableRemove', 'tableRows', 'tableColumns', 'tableCells', 'tableCellBackground', 'tableCellVerticalAlign', 'tableCellHorizontalAlign', tableBorderCommandName],
        
        events: Object.assign(this.events || {}, {
          'initialized': function() {
            const existingInit = (this.events && this.events.initialized) ? this.events.initialized : function() {};
            existingInit.call(this);
            console.log('🔍 Froala initialized - Table edit buttons:', this.opts.tableEditButtons);
            console.log('🔍 Table border command in array:', this.opts.tableEditButtons.includes('tableBorderFormat'));
          }
        }),
        
        toolbarButtons: webToolbar,
        
        quickInsertEnabled: true,
        quickInsertButtons: dropdownCommandName ? [dropdownCommandName, 'image', 'table', 'insertLink', 'ol', 'ul', 'hr', 'quote', 'video', 'code'] : ['image', 'table', 'insertLink', 'ol', 'ul', 'hr', 'quote', 'video', 'code'],
        quickInsertTags: ['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'pre', 'blockquote', 'li', 'td', 'th'], // Allow quick insert in all these tags even with content
        
        pluginsEnabled: ['mentions', 'link', 'quickInsert', 'image', 'table', 'lists', 'quote', 'code_view', 'video', 'hr', 'fontSize', 'colors'],

        enter: 1,

        linkInsertButtons: ['linkBack'],
        linkAlwaysBlank: false,
        linkAutoPrefix: 'https://',
        linkCheckBlank: true,
        linkCheckRelAttribute: false,
        linkEditButtons: ['linkOpen', 'linkEdit', 'linkRemove'],
        linkText: true,
        linkInsertCallback: function (link) {
          console.log('Link inserted:', link);
        },
        linkClickCallback: function (link) {
          return false;
        },
        linkList: [],
        linkMultipleStyles: false,
        linkNoFollow: false,

        // Quick insert link configuration
        quickInsertLinkButtons: ['linkBack', '|', 'linkByURL'],
        quickInsertLinkAlwaysBlank: true,
        quickInsertLinkAutoPrefix: 'https://',

        // Video configuration
        videoInsertButtons: ['videoBack', '|', 'videoByURL', 'videoEmbed'],
        videoUploadURL: '/froala_file_uploader',
        videoUploadParams: {
          authenticity_token: document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        },

        // Quote configuration
        quoteButtons: ['quoteBack', '|', 'quoteStyle'],

        fileUploadMethod: 'POST',
        imageUploadMethod: 'POST',
        enableMatchCase: true,

        mentions: {
          enabled: true,
          trigger: '@',
          minLength: 1,
          maxLength: 20,
          showOnMobile: true
        },
        
        events: {
          
          'initialized': function () {
            textArea.dataset.froalaInitialized = "true"; // Mark as initialized
            this.el.style.fontFamily = 'realist, arial, sans-serif';
            this.el.style.fontSize = '18px';
            this.el.style.color = '#575F67';
            
            // Debug: Log table edit buttons configuration
            console.log('🔍 ===== FROALA INITIALIZED =====');
            console.log('🔍 Table edit buttons:', this.opts.tableEditButtons);
            console.log('🔍 Table border command in array:', this.opts.tableEditButtons && this.opts.tableEditButtons.includes('tableBorderFormat'));
            // Check if command exists using Froala's API
            try {
              var commandExists = typeof this.commands !== 'undefined' && this.commands.tableBorderFormat !== undefined;
              console.log('🔍 Table border command available:', commandExists);
            } catch(e) {
              console.log('🔍 Table border command check skipped (commands API not available)');
            }
            
            // Apply dark theme class to the editor box for proper icon styling
            if (this.$box) {
              this.$box.addClass('dark-theme');
            }

            if (this.mentions && typeof this.mentions._init === 'function') {
              this.mentions._init();
            } else {
              if (window.FroalaEditor && window.FroalaEditor.PLUGINS && window.FroalaEditor.PLUGINS.mentions) {
                var mentionsPlugin = window.FroalaEditor.PLUGINS.mentions(this);
                if (mentionsPlugin && typeof mentionsPlugin._init === 'function') {
                  mentionsPlugin._init();
                  console.log('Mentions plugin manually initialized');
                }
              }
            }
            
            editorWrapper.classList.add('froala-active');
            this.el.style.cursor = 'text';
            
            // COMPREHENSIVE SOLUTION: Override quick insert plugin's internal logic
            const editor = this;
            
            // Centralized function to position quick insert relative to cursor
            // Define this BEFORE the event handlers so it's accessible to all of them
            let _noSelectionWarningShown = false; // Track if we've already warned about no selection
            const positionQuickInsertRelativeToCursor = (quickInsertElement, forcePosition = false) => {
              try {
                if (!quickInsertElement) {
                  return;
                }
                
                // CRITICAL: Check if we're already positioning to prevent double calls
                if (quickInsertElement._isPositioning && !forcePosition) {
                  return;
                }
                
                // CRITICAL: ALWAYS recalculate position based on current cursor
                // Never preserve old positions - cursor position changes when moving to new line or typing
                // This ensures the button always appears at the same position relative to the cursor
                // The only exception is when we're already positioning (to prevent double calls)
                
                // Find the content editable area first
                let editorContainer = null;
                let containerRect = null;
                const editorBox = editor.$box ? editor.$box.get(0) : null;
                
                if (editorBox) {
                  const contentEditable = editorBox.querySelector('[contenteditable="true"]');
                  if (contentEditable) {
                    editorContainer = contentEditable;
                    containerRect = contentEditable.getBoundingClientRect();
                    
                    // CRITICAL: Ensure content editable has position: relative for absolute positioning
                    const computedStyle = window.getComputedStyle(contentEditable);
                    if (computedStyle.position === 'static') {
                      contentEditable.style.position = 'relative';
                    }
                  } else {
                    editorContainer = editorBox;
                    containerRect = editorBox.getBoundingClientRect();
                    
                    // CRITICAL: Ensure editor box has position: relative for absolute positioning
                    const computedStyle = window.getComputedStyle(editorBox);
                    if (computedStyle.position === 'static') {
                      editorBox.style.position = 'relative';
                    }
                  }
                }
                
                if (!editorContainer || !containerRect) {
                  return;
                }
                
                // Try to get selection and cursor position
                let left = 0;
                let top = 0;
                let hasValidCursor = false;
                
                try {
                  // CRITICAL: Always try to get fresh selection - don't cache it
                  // Froala's selection.get() returns native Selection object
                  // Use anchorNode/anchorOffset instead of startContainer/startOffset
                  const selection = editor.selection.get();
                  console.log('🔍 Selection (forcePosition=' + forcePosition + '):', selection);
                  
                  // Get the container node - use anchorNode (native Selection) or startContainer (Froala internal)
                  const containerNode = selection?.anchorNode || selection?.startContainer;
                  const offset = selection?.anchorOffset !== undefined ? selection.anchorOffset : selection?.startOffset;
                  
                  console.log('📍 Container node:', containerNode);
                  console.log('📍 Offset:', offset);
                  
                  if (selection && containerNode && offset !== undefined) {
                    console.log('✅ Selection has container node and offset');
                    
                    // Create a range at the cursor position
                    const range = document.createRange();
                    try {
                      range.setStart(containerNode, offset);
                      range.setEnd(containerNode, offset);
                    } catch (rangeError) {
                      console.error('❌ Range error:', rangeError, { containerNode, offset });
                      throw rangeError;
                    }
                    
                    // Get cursor position in viewport coordinates
                    const cursorRect = range.getBoundingClientRect();
                    console.log('📐 Cursor rect:', cursorRect);
                    
                    // CRITICAL: Check if cursor rect is valid
                    // Even if width/height are 0, we can still use the position
                    // A valid cursor rect should have valid coordinates
                    if (cursorRect && 
                        (cursorRect.width >= 0 && cursorRect.height >= 0) &&
                        (cursorRect.left !== undefined && cursorRect.top !== undefined) &&
                        !isNaN(cursorRect.left) && !isNaN(cursorRect.top) &&
                        cursorRect.left !== 0 && cursorRect.top !== 0) { // Also check that it's not at 0,0 (invalid)
                      console.log('✅ Valid cursor rect found:', {
                        left: cursorRect.left,
                        top: cursorRect.top,
                        right: cursorRect.right,
                        bottom: cursorRect.bottom,
                        width: cursorRect.width,
                        height: cursorRect.height
                      });
                      // CRITICAL: Position button right after the caret (Option A: overlay button)
                      // Use rect.right to position after the caret, not rect.left
                      const gap = 20; // Gap in px after the caret
                      
                      // rect.left/rect.top are in viewport coords. convert to container coords
                      // Position after the caret: use rect.right instead of rect.left
                      left = cursorRect.right - containerRect.left + gap;
                      
                      // Vertically center relative to caret height
                      // Use (rect.top + rect.bottom) / 2 to center vertically on the caret
                      // If height is 0, use top position directly
                      const caretCenterY = cursorRect.height > 0 
                        ? (cursorRect.top + cursorRect.bottom) / 2 
                        : cursorRect.top;
                      top = caretCenterY - containerRect.top;
                      
                      // Account for container padding and border
                      const containerComputedStyle = window.getComputedStyle(editorContainer);
                      const containerPaddingTop = parseFloat(containerComputedStyle.paddingTop) || 0;
                      const containerBorderTop = parseFloat(containerComputedStyle.borderTopWidth) || 0;
                      const containerPaddingLeft = parseFloat(containerComputedStyle.paddingLeft) || 0;
                      const containerBorderLeft = parseFloat(containerComputedStyle.borderLeftWidth) || 0;
                      
                      // Adjust for container padding and border
                      left = left - containerPaddingLeft - containerBorderLeft;
                      top = top - containerPaddingTop - containerBorderTop;
                      
                      // CRITICAL: Adjust top position to be 10-15px more (move button down)
                      // This makes the button appear slightly below the cursor for better visibility
                      top = top + 12; // Add 12px (middle of 10-15px range)
                      
                      // CRITICAL: Check if quick insert toolbar would overflow the viewport/container
                      // If it would overflow, position it on a new line below the cursor
                      if (quickInsertElement && hasValidCursor) {
                        // Temporarily set position to measure width
                        const tempPosition = quickInsertElement.style.position;
                        const tempLeft = quickInsertElement.style.left;
                        const tempTop = quickInsertElement.style.top;
                        const tempDisplay = quickInsertElement.style.display;
                        const tempVisibility = quickInsertElement.style.visibility;
                        const tempOpacity = quickInsertElement.style.opacity;
                        const tempZIndex = quickInsertElement.style.zIndex;
                        const tempPointerEvents = quickInsertElement.style.pointerEvents;
                        
                        // Make element temporarily visible to measure its width
                        quickInsertElement.style.position = 'absolute';
                        quickInsertElement.style.left = left + 'px';
                        quickInsertElement.style.top = top + 'px';
                        quickInsertElement.style.display = 'flex';
                        quickInsertElement.style.visibility = 'visible';
                        quickInsertElement.style.opacity = '0'; // Make invisible but still measurable
                        quickInsertElement.style.zIndex = '9999';
                        quickInsertElement.style.pointerEvents = 'none'; // Prevent interaction during measurement
                        
                        // Force a reflow to ensure width is calculated
                        void quickInsertElement.offsetWidth;
                        
                        // Get the actual width of the quick insert element (synchronously)
                        const quickInsertRect = quickInsertElement.getBoundingClientRect();
                        const quickInsertWidth = quickInsertRect.width || quickInsertElement.offsetWidth || quickInsertElement.scrollWidth || 0;
                        
                        // Get viewport width (what's actually visible on screen)
                        const viewportWidth = window.innerWidth;
                        
                        // Get container width (accounting for padding)
                        const containerWidth = containerRect.width;
                        const containerPaddingRight = parseFloat(containerComputedStyle.paddingRight) || 0;
                        const containerBorderRight = parseFloat(containerComputedStyle.borderRightWidth) || 0;
                        const availableWidth = containerWidth - containerPaddingLeft - containerPaddingRight - containerBorderLeft - containerBorderRight;
                        
                        // Calculate the right edge of the toolbar in viewport coordinates
                        const toolbarRightEdge = cursorRect.right + gap;
                        
                        // Check if toolbar would overflow to the right (check both viewport and container)
                        const wouldOverflowViewport = toolbarRightEdge + quickInsertWidth > viewportWidth;
                        const wouldOverflowContainer = left + quickInsertWidth > availableWidth;
                        
                        if (quickInsertWidth > 0 && (wouldOverflowViewport || wouldOverflowContainer)) {
                          // Position toolbar on a new line below the cursor
                          // Position at the start of the line (left edge of container with padding)
                          left = containerPaddingLeft + containerBorderLeft;
                          
                          // Position below the cursor (add line height to top position)
                          // Get line height from the cursor's computed style
                          let lineHeight = 20; // Default line height
                          try {
                            const cursorNode = containerNode.nodeType === Node.TEXT_NODE 
                              ? containerNode.parentElement 
                              : containerNode;
                            if (cursorNode) {
                              const cursorComputedStyle = window.getComputedStyle(cursorNode);
                              const lineHeightValue = cursorComputedStyle.lineHeight;
                              if (lineHeightValue && lineHeightValue !== 'normal') {
                                lineHeight = parseFloat(lineHeightValue) || 20;
                              } else {
                                // If line-height is 'normal', use font-size * 1.2 as fallback
                                const fontSize = parseFloat(cursorComputedStyle.fontSize) || 16;
                                lineHeight = fontSize * 1.2;
                              }
                            }
                          } catch (e) {
                            // Use default if calculation fails
                          }
                          
                          // Position below the cursor with some spacing
                          top = top + lineHeight; // Add line height + 8px spacing
                          
                          console.log('🔄 Toolbar would overflow, repositioning to new line:', {
                            quickInsertWidth,
                            viewportWidth,
                            availableWidth,
                            toolbarRightEdge,
                            newLeft: left,
                            newTop: top
                          });
                        }
                        
                        // Restore temporary styles (they'll be set properly below)
                        quickInsertElement.style.position = tempPosition;
                        quickInsertElement.style.left = tempLeft;
                        quickInsertElement.style.top = tempTop;
                        quickInsertElement.style.display = tempDisplay;
                        quickInsertElement.style.visibility = tempVisibility;
                        quickInsertElement.style.opacity = tempOpacity;
                        quickInsertElement.style.zIndex = tempZIndex;
                        quickInsertElement.style.pointerEvents = tempPointerEvents;
                      }
                      
                      hasValidCursor = true;
                      _noSelectionWarningShown = false; // Reset warning flag on success
                      console.log('✅ Calculated position:', { left, top, hasValidCursor });
                    } else {
                      console.warn('⚠️ Invalid cursor rect:', cursorRect);
                    }
                  } else {
                    console.warn('⚠️ No selection or container node:', { 
                      selection, 
                      hasAnchorNode: !!selection?.anchorNode,
                      hasStartContainer: !!selection?.startContainer,
                      anchorNode: selection?.anchorNode,
                      startContainer: selection?.startContainer
                    });
                  }
                } catch (e) {
                  console.error('❌ Selection error:', e);
                  // Selection error - return early, never use fallback positioning
                }
                
                // If no valid cursor, use fallback positioning at the start of the editor
                // BUT: When forcePosition is true (typing), NEVER use fallback - always wait for cursor
                if (!hasValidCursor) {
                  // If forcePosition is true, we're typing - cursor MUST be available
                  // Don't use fallback positioning when typing - wait for cursor position
                  if (forcePosition) {
                    // When typing, cursor should be available - if not, return early
                    // The event handler will retry with double requestAnimationFrame
                    // This ensures we always use cursor position, never fallback when typing
                    return;
                  }
                  
                  // Check if we already have valid cursor-based styles - if so, preserve them
                  const existingTop = quickInsertElement.style.top;
                  const existingLeft = quickInsertElement.style.left;
                  const computedTop = window.getComputedStyle(quickInsertElement).top;
                  const computedLeft = window.getComputedStyle(quickInsertElement).left;
                  
                  // Check if existing position is fallback (19px range)
                  const topValue = parseFloat(existingTop || computedTop) || 0;
                  const leftValue = parseFloat(existingLeft || computedLeft) || 0;
                  const isFallbackPosition = (topValue >= 9 && topValue <= 29) && (leftValue >= 9 && leftValue <= 29);
                  
                  // If we have valid cursor-based styles (not fallback), preserve them
                  if ((existingTop || computedTop) && 
                      (existingTop !== 'auto' && computedTop !== 'auto') &&
                      !existingTop?.includes('-9999') && !computedTop?.includes('-9999') &&
                      !isFallbackPosition) {
                    return; // Preserve existing cursor-based position
                  }
                  
                  // Use fallback positioning at the start of the editor content
                  // Only use fallback when NOT typing (forcePosition = false)
                  const containerComputedStyle = window.getComputedStyle(editorContainer);
                  const containerPaddingTop = parseFloat(containerComputedStyle.paddingTop) || 0;
                  const containerBorderTop = parseFloat(containerComputedStyle.borderTopWidth) || 0;
                  const containerPaddingLeft = parseFloat(containerComputedStyle.paddingLeft) || 0;
                  
                  left = containerPaddingLeft + 10; // Small offset from left edge
                  top = containerPaddingTop + containerBorderTop + 10; // Small offset from top edge
                }
                
                // CRITICAL: Ensure we have valid numeric values
                if (isNaN(left) || isNaN(top)) {
                  return;
                }
                
                const editorBoxElement = editor.$box ? editor.$box.get(0) : null;
                const currentParent = quickInsertElement.parentElement;
                
                if (editorBoxElement && currentParent !== editorBoxElement) {
                  if (editorContainer && editorContainer.contains(quickInsertElement)) {
                    editorContainer.removeChild(quickInsertElement);
                  }
                  editorBoxElement.appendChild(quickInsertElement);
                } else if (!editorBoxElement && currentParent !== editorContainer) {
                  editorContainer.appendChild(quickInsertElement);
                }
                
                quickInsertElement.style.position = 'absolute';
                quickInsertElement.style.left = left + 'px';
                quickInsertElement.style.top = top + 'px';
                quickInsertElement.style.margin = '0';
                quickInsertElement.style.zIndex = '9999';
                quickInsertElement.style.display = 'flex';
                quickInsertElement.style.visibility = 'visible';
                quickInsertElement.style.opacity = '1';
                
                if (hasValidCursor) {
                  quickInsertElement.style.transform = 'translateY(-50%)';
                } else {
                  quickInsertElement.style.transform = 'none';
                }
                
                try {
                  const editable = editorContainer.querySelector('.fr-view') || editorContainer;
                  if (editable) {
                    const cs = window.getComputedStyle(editable);
                    const fontSize = cs.fontSize;
                    const lineHeight = cs.lineHeight;
                    if (fontSize) {
                      quickInsertElement.style.fontSize = fontSize;
                    }
                    if (lineHeight) {
                      quickInsertElement.style.lineHeight = lineHeight;
                    }
                    const caretColor = cs.caretColor || cs.color;
                    if (caretColor) {
                      quickInsertElement.style.color = caretColor;
                    }
                  }
                } catch (e) {
                }
                
                quickInsertElement.style.setProperty('position', 'absolute', 'important');
                quickInsertElement.style.setProperty('left', left + 'px', 'important');
                quickInsertElement.style.setProperty('top', top + 'px', 'important');
                quickInsertElement.style.setProperty('margin', '0', 'important');
                quickInsertElement.style.setProperty('z-index', '9999', 'important');
                quickInsertElement.style.setProperty('display', 'flex', 'important');
                quickInsertElement.style.setProperty('visibility', 'visible', 'important');
                quickInsertElement.style.setProperty('opacity', '1', 'important');
                
                if (hasValidCursor) {
                  quickInsertElement.style.setProperty('transform', 'translateY(-50%)', 'important');
                } else {
                  quickInsertElement.style.setProperty('transform', 'none', 'important');
                }
                
                const verifyTop = quickInsertElement.style.top;
                const verifyLeft = quickInsertElement.style.left;
                if (!verifyTop || !verifyLeft) {
                  console.error('❌ Styles not set!', { verifyTop, verifyLeft, left, top });
                  quickInsertElement.style.top = top + 'px';
                  quickInsertElement.style.left = left + 'px';
                }
                
                quickInsertElement.setAttribute('data-preserved-top', top + 'px');
                quickInsertElement.setAttribute('data-preserved-left', left + 'px');
                
                
              } catch (e) {
                console.error('❌ Failed to position quick insert relative to cursor:', e);
              }
            };
            
            editor.positionQuickInsertRelativeToCursor = positionQuickInsertRelativeToCursor;
            
            if (this.quickInsert) {
              this.quickInsert._allowHide = false;
              this.quickInsert._keepVisible = true;
              
              if (typeof this.quickInsert.hide === 'function') {
                this.quickInsert._originalHide = this.quickInsert.hide.bind(this.quickInsert);
              }
              if (typeof this.quickInsert.show === 'function') {
                this.quickInsert._originalShow = this.quickInsert.show.bind(this.quickInsert);
              }
              
              this.quickInsert.show = function() {
                const textArea = editor.$el ? editor.$el.get(0) : null;
                if (!textArea || !textArea.dataset.froalaInitialized) {
                  console.log('⚠️ Editor not initialized, not showing quick insert');
                  return;
                }
                
                try {
                  const selection = editor.selection.get();
                  const containerNode = selection?.anchorNode || selection?.startContainer;
                  const offset = selection?.anchorOffset !== undefined ? selection.anchorOffset : selection?.startOffset;
                  
                  if (!selection || !containerNode || offset === undefined) {
                    console.log('⚠️ No active cursor/selection, not showing quick insert');
                    return;
                  }
                  
                  const editorBox = editor.$box ? editor.$box.get(0) : null;
                  if (editorBox && !editorBox.contains(containerNode)) {
                    console.log('⚠️ Selection is not within editor, not showing quick insert');
                    return;
                  }
                } catch (e) {
                  console.log('⚠️ Error checking selection, not showing quick insert:', e);
                  return;
                }
                
                const allQuickInsertElements = editor.$box ? editor.$box.find('.fr-quick-insert').toArray() : [];
                allQuickInsertElements.forEach(element => {
                  element.classList.remove('fr-visible');
                  element.classList.add('fr-hidden');
                  element.style.setProperty('display', 'none', 'important');
                  element.style.setProperty('visibility', 'hidden', 'important');
                  element.style.setProperty('opacity', '0', 'important');
                });
                
                let quickInsertElement = editor.$box ? editor.$box.find('.fr-quick-insert').get(0) : null;
                
                if (!quickInsertElement) {
                  if (this._originalShow) {
                    this._originalShow();
                  }
                  quickInsertElement = editor.$box ? editor.$box.find('.fr-quick-insert').get(0) : null;
                  if (!quickInsertElement) {
                    return;
                  }
                }
                
                const quickInsertElements = editor.$box ? editor.$box.find('.fr-quick-insert').toArray() : [];
                if (quickInsertElements.length > 1) {
                  console.warn('⚠️ Multiple quick insert buttons found in show(), removing duplicates');
                  quickInsertElements.slice(1).forEach(duplicate => {
                    duplicate.remove();
                  });
                  quickInsertElement = quickInsertElements[0];
                }
                
                const computedStyle = window.getComputedStyle(quickInsertElement);
                let savedTop = computedStyle.top;
                let savedLeft = computedStyle.left;
                
                if (!savedTop || savedTop === 'auto' || savedTop.includes('-9999')) {
                  savedTop = quickInsertElement.getAttribute('data-preserved-top');
                }
                if (!savedLeft || savedLeft === 'auto' || savedLeft.includes('-9999')) {
                  savedLeft = quickInsertElement.getAttribute('data-preserved-left');
                }
                
                if (this._originalShow) {
                  this._originalShow();
                }
                
                const allButtonsAfterShow = editor.$box ? editor.$box.find('.fr-quick-insert').toArray() : [];
                if (allButtonsAfterShow.length > 1) {
                  console.warn('⚠️ Multiple quick insert buttons after show(), removing duplicates');
                  allButtonsAfterShow.slice(1).forEach(duplicate => {
                    duplicate.remove(); // Remove duplicates completely
                  });
                  quickInsertElement = allButtonsAfterShow[0];
                }
                
                const needsRepositioning = !savedTop || savedTop === 'auto' || savedTop.includes('-9999') ||
                                          !savedLeft || savedLeft === 'auto' || savedLeft.includes('-9999');
                
                // CRITICAL: Always recalculate position based on current cursor
                // Never preserve old positions - cursor position changes when moving to new line or typing
                if (editor.positionQuickInsertRelativeToCursor) {
                  editor.positionQuickInsertRelativeToCursor(quickInsertElement, true);
                } else if (typeof positionQuickInsertRelativeToCursor === 'function') {
                  positionQuickInsertRelativeToCursor(quickInsertElement, true);
                }
              };
              
              if (typeof this.quickInsert.updatePosition === 'function') {
                this.quickInsert._originalUpdatePosition = this.quickInsert.updatePosition.bind(this.quickInsert);
                this.quickInsert.updatePosition = function() {
                  if (this._originalUpdatePosition) {
                    this._originalUpdatePosition();
                  }
                  
                  if (this._keepVisible) {
                    const quickInsertElement = editor.$box ? editor.$box.find('.fr-quick-insert').get(0) : null;
                    if (quickInsertElement) {
                      quickInsertElement.classList.add('fr-visible');
                      quickInsertElement.style.setProperty('display', 'flex', 'important');
                      quickInsertElement.style.setProperty('visibility', 'visible', 'important');
                      quickInsertElement.style.setProperty('opacity', '1', 'important');
                    }
                  }
                };
              }
              
              this.quickInsert.hide = function() {
                const quickInsertElements = editor.$box ? editor.$box.find('.fr-quick-insert').toArray() : [];
                quickInsertElements.forEach(element => {
                  element.classList.remove('fr-visible');
                  element.classList.add('fr-hidden');
                  element.style.setProperty('display', 'none', 'important');
                  element.style.setProperty('visibility', 'hidden', 'important');
                  element.style.setProperty('opacity', '0', 'important');
                });
                
                if (!this._keepVisible || this._allowHide) {
                  if (this._originalHide) {
                    this._originalHide();
                  }
                }
              };
              
              this.quickInsert.allowHide = function() {
                this._allowHide = true;
                this._keepVisible = false;
              };
              
              this.quickInsert.preventHide = function() {
                this._allowHide = false;
                this._keepVisible = true;
              };
              
              if (this.quickInsert._checkVisibility) {
                const originalCheck = this.quickInsert._checkVisibility.bind(this.quickInsert);
                this.quickInsert._checkVisibility = function() {
                  if (this._keepVisible) {
                    if (this.show) {
                      this.show();
                    }
                    return true;
                  }
                  return originalCheck();
                };
              }
              
              if (this.quickInsert._isLineEmpty) {
                const originalIsEmpty = this.quickInsert._isLineEmpty.bind(this.quickInsert);
                this.quickInsert._isLineEmpty = function() {
                  if (this._keepVisible) {
                    return true;
                  }
                  return originalIsEmpty();
                };
              }
            }
            
              const originalSelectionRestore = this.selection.restore;
              this.selection.restore = function() {
                const result = originalSelectionRestore.apply(this, arguments);
                
                setTimeout(() => {
                  if (editor.quickInsert && editor.quickInsert._keepVisible) {
                    if (editor.quickInsert.show) {
                      editor.quickInsert.show();
                    }
                    const quickInsertElement = editor.$box ? editor.$box.find('.fr-quick-insert').get(0) : null;
                    if (quickInsertElement) {
                      quickInsertElement.classList.add('fr-visible');
                      
                      requestAnimationFrame(() => {
                        // CRITICAL: Always recalculate position based on current cursor
                        if (editor.positionQuickInsertRelativeToCursor) {
                          editor.positionQuickInsertRelativeToCursor(quickInsertElement, true);
                        } else if (typeof positionQuickInsertRelativeToCursor === 'function') {
                          positionQuickInsertRelativeToCursor(quickInsertElement, true);
                        }
                      });
                    }
                  }
                }, 10);
                
                return result;
              };
            
            const handleSelectionChange = () => {
              if (editor.quickInsert && editor.quickInsert._keepVisible) {
                const quickInsertElement = editor.$box ? editor.$box.find('.fr-quick-insert').get(0) : null;
                if (quickInsertElement && !quickInsertElement._isPositioning) {
                  try {
                    const selection = editor.selection.get();
                    if (selection && selection.startContainer) {
                      const editorBox = editor.$box ? editor.$box.get(0) : null;
                      if (editorBox && editorBox.contains(selection.startContainer)) {
                        quickInsertElement._isPositioning = true;
                        requestAnimationFrame(() => {
                          try {
                            if (editor.positionQuickInsertRelativeToCursor) {
                              editor.positionQuickInsertRelativeToCursor(quickInsertElement, true);
                            } else if (typeof positionQuickInsertRelativeToCursor === 'function') {
                              positionQuickInsertRelativeToCursor(quickInsertElement, true);
                            }
                          } finally {
                            quickInsertElement._isPositioning = false;
                          }
                        });
                      }
                    }
                  } catch (e) {
                    // Ignore errors
                  }
                }
              }
            };
            
            document.addEventListener('selectionchange', handleSelectionChange);
            
            editor._selectionChangeHandler = handleSelectionChange;
            
            setTimeout(() => {
              const allQuickInsertElements = editor.$box ? editor.$box.find('.fr-quick-insert').toArray() : [];
              
              const contentEditable = editor.$box ? editor.$box.find('[contenteditable="true"]').get(0) : null;
              allQuickInsertElements.forEach(button => {
                if (contentEditable && contentEditable.contains(button)) {
                  console.warn('⚠️ Quick insert button found in content editable, removing it');
                  button.remove();
                }
              });
              
              const remainingButtons = editor.$box ? editor.$box.find('.fr-quick-insert').toArray() : [];
              if (remainingButtons.length > 1) {
                console.warn('⚠️ Multiple quick insert buttons found, removing duplicates');
                remainingButtons.slice(1).forEach(duplicate => {
                  duplicate.remove();
                });
              }
              
              const quickInsertElement = editor.$box ? editor.$box.find('.fr-quick-insert').get(0) : null;
              if (quickInsertElement) {
                if (contentEditable && contentEditable.contains(quickInsertElement)) {
                  console.warn('⚠️ Quick insert button is in content editable, moving to editor box');
                  const editorBoxElement = editor.$box ? editor.$box.get(0) : null;
                  if (editorBoxElement) {
                    contentEditable.removeChild(quickInsertElement);
                    editorBoxElement.appendChild(quickInsertElement);
                  }
                }
                const preventOffScreenPositioning = () => {
                  const computedStyle = window.getComputedStyle(quickInsertElement);
                  const currentTop = computedStyle.top;
                  const currentLeft = computedStyle.left;
                  const inlineTop = quickInsertElement.style.top || '';
                  const inlineLeft = quickInsertElement.style.left || '';
                  
                  if (currentTop === '-9999px' || currentLeft === '-9999px' ||
                      inlineTop.includes('-9999') || inlineLeft.includes('-9999')) {
                    quickInsertElement.style.removeProperty('top');
                    quickInsertElement.style.removeProperty('left');
                    // CRITICAL: Always recalculate position based on current cursor
                    if (editor.positionQuickInsertRelativeToCursor) {
                      editor.positionQuickInsertRelativeToCursor(quickInsertElement, true);
                    } else if (typeof positionQuickInsertRelativeToCursor === 'function') {
                      positionQuickInsertRelativeToCursor(quickInsertElement, true);
                    }
                  }
                };
                
                preventOffScreenPositioning();
                // CRITICAL: Always recalculate position based on current cursor
                if (editor.positionQuickInsertRelativeToCursor) {
                  editor.positionQuickInsertRelativeToCursor(quickInsertElement, true);
                } else if (typeof positionQuickInsertRelativeToCursor === 'function') {
                  positionQuickInsertRelativeToCursor(quickInsertElement, true);
                }
                
                let checkCount = 0;
                const maxChecks = 100; // Stop after 100 checks (1 second at 10ms intervals)
                const immediateCheck = setInterval(() => {
                  checkCount++;
                  if (checkCount > maxChecks) {
                    clearInterval(immediateCheck);
                    return;
                  }
                  
                  const computedStyle = window.getComputedStyle(quickInsertElement);
                  const inlineTop = quickInsertElement.style.top || '';
                  const inlineLeft = quickInsertElement.style.left || '';
                  
                  if (computedStyle.top === '-9999px' || computedStyle.left === '-9999px' ||
                      inlineTop.includes('-9999') || inlineLeft.includes('-9999')) {
                    quickInsertElement.style.removeProperty('top');
                    quickInsertElement.style.removeProperty('left');
                    if (editor.positionQuickInsertRelativeToCursor) {
                      editor.positionQuickInsertRelativeToCursor(quickInsertElement, true);
                    } else if (typeof positionQuickInsertRelativeToCursor === 'function') {
                      positionQuickInsertRelativeToCursor(quickInsertElement, true);
                    }
                  }
                }, 10); // Check every 10ms
                
                const captureInitialTop = () => {
                  const inlineTop = quickInsertElement.style.top || '';
                  const computedStyle = window.getComputedStyle(quickInsertElement);
                  const computedTop = computedStyle.top;
                  
                  let currentTop = inlineTop;
                  if (!currentTop || currentTop === 'auto' || currentTop === '' || currentTop.includes('-9999')) {
                    if (computedTop && computedTop !== 'auto' && !computedTop.includes('-9999')) {
                      currentTop = computedTop;
                    }
                  }
                  
                  if (currentTop && currentTop !== 'auto' && !currentTop.includes('-9999')) {
                    quickInsertElement.setAttribute('data-preserved-top', currentTop);
                  }
                };
                
                captureInitialTop();
                
                const observer = new MutationObserver((mutations) => {
                  if (editor.quickInsert && editor.quickInsert._keepVisible) {
                    mutations.forEach((mutation) => {
                      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                        const currentClasses = quickInsertElement.className || '';
                        if (!currentClasses.includes('fr-visible')) {
                          quickInsertElement.classList.add('fr-visible');
                        }
                        if (currentClasses.includes('fr-visible')) {
                          captureInitialTop();
                        }
                      }
                      
                      if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
                        const style = quickInsertElement.getAttribute('style') || '';
                        const computedStyle = window.getComputedStyle(quickInsertElement);
                        const currentTop = computedStyle.top;
                        const currentLeft = computedStyle.left;
                        
                        if (currentTop === '-9999px' || currentLeft === '-9999px' ||
                            style.includes('top: -9999px') || style.includes('top:-9999px') ||
                            style.includes('left: -9999px') || style.includes('left:-9999px')) {
                          quickInsertElement.style.removeProperty('top');
                          quickInsertElement.style.removeProperty('left');
                          // CRITICAL: Always recalculate position based on current cursor
                          if (editor.positionQuickInsertRelativeToCursor) {
                            editor.positionQuickInsertRelativeToCursor(quickInsertElement, true);
                          } else if (typeof positionQuickInsertRelativeToCursor === 'function') {
                            positionQuickInsertRelativeToCursor(quickInsertElement, true);
                          }
                          quickInsertElement.classList.add('fr-visible');
                          return; // Don't continue with other checks
                        }
                        
                        const hasTop = currentTop && currentTop !== 'auto' && !currentTop.includes('-9999') && currentTop !== '0px';
                        const hasLeft = currentLeft && currentLeft !== 'auto' && !currentLeft.includes('-9999') && currentLeft !== '0px';
                        
                        if ((!hasTop || !hasLeft) && !quickInsertElement._isPositioning) {
                          const currentTopValue = parseFloat(currentTop) || 0;
                          const currentLeftValue = parseFloat(currentLeft) || 0;
                          const isFallbackPosition = (currentTopValue >= 9 && currentTopValue <= 29) && 
                                                   (currentLeftValue >= 9 && currentLeftValue <= 29);
                          
                          try {
                            const selection = editor.selection.get();
                            if (!selection || !selection.startContainer) {
                              return;
                            }
                            
                            quickInsertElement._isPositioning = true;
                            requestAnimationFrame(() => {
                              try {
                                if (editor.positionQuickInsertRelativeToCursor) {
                                  editor.positionQuickInsertRelativeToCursor(quickInsertElement, true);
                                } else if (typeof positionQuickInsertRelativeToCursor === 'function') {
                                  positionQuickInsertRelativeToCursor(quickInsertElement, true);
                                }
                              } finally {
                                quickInsertElement._isPositioning = false;
                              }
                            });
                          } catch (e) {
                            return;
                          }
                        }
                        
                        if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden' || 
                            computedStyle.opacity === '0' || style.includes('display: none')) {
                          if (editor.quickInsert.show) {
                            editor.quickInsert.show();
                          }
                          quickInsertElement.style.setProperty('display', 'flex', 'important');
                          quickInsertElement.style.setProperty('visibility', 'visible', 'important');
                          quickInsertElement.style.setProperty('opacity', '1', 'important');
                          quickInsertElement.classList.add('fr-visible');
                          requestAnimationFrame(() => {
                            // CRITICAL: Always recalculate position based on current cursor
                            if (editor.positionQuickInsertRelativeToCursor) {
                              editor.positionQuickInsertRelativeToCursor(quickInsertElement, true);
                            } else if (typeof positionQuickInsertRelativeToCursor === 'function') {
                              positionQuickInsertRelativeToCursor(quickInsertElement, true);
                            }
                          });
                        }
                      }
                    });
                  }
                });
                
                observer.observe(quickInsertElement, {
                  attributes: true,
                  attributeFilter: ['style', 'class'],
                  childList: true,
                  subtree: true
                });
                
                editor.quickInsert._observer = observer;
              }
            }, 300);
            
            let styleMonitorFrame = null;
            let monitorActive = false;
            const startStyleMonitor = () => {
              if (monitorActive) return; // Prevent multiple monitors
              monitorActive = true;
              
              const monitor = () => {
                if (!editor.quickInsert || !editor.quickInsert._keepVisible) {
                  monitorActive = false;
                  if (styleMonitorFrame) {
                    cancelAnimationFrame(styleMonitorFrame);
                  }
                  return;
                }
                
                const quickInsertElement = editor.$box ? editor.$box.find('.fr-quick-insert').get(0) : null;
                if (!quickInsertElement) {
                  monitorActive = false;
                  if (styleMonitorFrame) {
                    cancelAnimationFrame(styleMonitorFrame);
                  }
                  return;
                }
                
                if (!quickInsertElement.classList.contains('fr-visible')) {
                  styleMonitorFrame = requestAnimationFrame(monitor);
                  return;
                }
                
                const computedStyle = window.getComputedStyle(quickInsertElement);
                const currentTop = computedStyle.top;
                const currentLeft = computedStyle.left;
                const inlineTop = quickInsertElement.style.top || '';
                const inlineLeft = quickInsertElement.style.left || '';
                
                if (currentTop === '-9999px' || currentLeft === '-9999px' ||
                    inlineTop.includes('-9999') || inlineLeft.includes('-9999')) {
                  quickInsertElement.style.removeProperty('top');
                  quickInsertElement.style.removeProperty('left');
                  if (editor.positionQuickInsertRelativeToCursor) {
                    editor.positionQuickInsertRelativeToCursor(quickInsertElement, true);
                  } else if (typeof positionQuickInsertRelativeToCursor === 'function') {
                    positionQuickInsertRelativeToCursor(quickInsertElement, true);
                  }
                }
                
                const hasTop = currentTop && currentTop !== 'auto' && !currentTop.includes('-9999') && currentTop !== '0px';
                const hasLeft = currentLeft && currentLeft !== 'auto' && !currentLeft.includes('-9999') && currentLeft !== '0px';
                
                if ((!hasTop || !hasLeft) && !quickInsertElement._isPositioning) {
                  try {
                    const selection = editor.selection.get();
                    if (selection && selection.startContainer) {
                      quickInsertElement._isPositioning = true;
                      try {
                        if (editor.positionQuickInsertRelativeToCursor) {
                          editor.positionQuickInsertRelativeToCursor(quickInsertElement, true);
                        } else if (typeof positionQuickInsertRelativeToCursor === 'function') {
                          positionQuickInsertRelativeToCursor(quickInsertElement, true);
                        }
                      } finally {
                        quickInsertElement._isPositioning = false;
                      }
                    }
                  } catch (e) {
                    quickInsertElement._isPositioning = false;
                  }
                }
                
                if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden' || 
                    computedStyle.opacity === '0') {
                  if (editor.quickInsert.show) {
                    editor.quickInsert.show();
                  }
                  quickInsertElement.style.setProperty('display', 'flex', 'important');
                  quickInsertElement.style.setProperty('visibility', 'visible', 'important');
                  quickInsertElement.style.setProperty('opacity', '1', 'important');
                  quickInsertElement.classList.add('fr-visible');
                }
                
                styleMonitorFrame = requestAnimationFrame(monitor);
              };
              
              monitor();
            };
            
            if (editor.quickInsert && editor.quickInsert._keepVisible) {
              startStyleMonitor();
            }
            
            if (editor.quickInsert) {
              editor.quickInsert._styleMonitorFrame = styleMonitorFrame;
            }
            
            const keepVisibleInterval = setInterval(() => {
              if (editor.quickInsert && editor.quickInsert._keepVisible) {
                const quickInsertElement = editor.$box ? editor.$box.find('.fr-quick-insert').get(0) : null;
                if (quickInsertElement) {
                  const computedStyle = window.getComputedStyle(quickInsertElement);
                  const hasVisibleClass = quickInsertElement.classList.contains('fr-visible');
                  
                  if (!hasVisibleClass) {
                    quickInsertElement.classList.add('fr-visible');
                  }
                  
                  const currentTop = computedStyle.top;
                  const currentLeft = computedStyle.left;
                  const hasTop = currentTop && currentTop !== 'auto' && !currentTop.includes('-9999') && currentTop !== '0px';
                  const hasLeft = currentLeft && currentLeft !== 'auto' && !currentLeft.includes('-9999') && currentLeft !== '0px';
                  
                  if ((!hasTop || !hasLeft) && !quickInsertElement._isPositioning) {
                    try {
                      const selection = editor.selection.get();
                      if (selection && selection.startContainer) {
                        quickInsertElement._isPositioning = true;
                        try {
                          if (editor.positionQuickInsertRelativeToCursor) {
                            editor.positionQuickInsertRelativeToCursor(quickInsertElement, true);
                          } else if (typeof positionQuickInsertRelativeToCursor === 'function') {
                            positionQuickInsertRelativeToCursor(quickInsertElement, true);
                          }
                        } finally {
                          quickInsertElement._isPositioning = false;
                        }
                      }
                    } catch (e) {
                      quickInsertElement._isPositioning = false;
                    }
                  }
                  
                  if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden' || 
                      computedStyle.opacity === '0') {
                    if (editor.quickInsert.show) {
                      editor.quickInsert.show();
                    }
                    quickInsertElement.style.setProperty('display', 'flex', 'important');
                    quickInsertElement.style.setProperty('visibility', 'visible', 'important');
                    quickInsertElement.style.setProperty('opacity', '1', 'important');
                    quickInsertElement.classList.add('fr-visible');
                  }
                }
              }
            }, 50);
            
            if (editor.quickInsert) {
              editor.quickInsert._keepVisibleInterval = keepVisibleInterval;
            }
            
            console.log('🔍 initialized event: Editor fully initialized');
          },

          'image.inserted': function (img, response) {
            console.log("✔️ Image inserted into editor", img, response);
            if (window.currentFroalaController) {
              window.currentFroalaController.markOperationComplete();
            }
          },

          'table.inserted': function (table, response) {
            console.log("✔️ Table inserted into editor", table, response);
            // Set default 0.5px borders for newly inserted tables
            const cells = table.querySelectorAll('td, th');
            cells.forEach(function(cell) {
              cell.style.border = '0.5px solid #ddd';
            });
            table.style.border = '0.5px solid #ddd';
          },

          'video.inserted': function (video, response) {
            console.log("✔️ Video inserted into editor", video, response);
          },

          'link.inserted': function (link, response) {
            console.log("✔️ Link inserted into editor", link, response);
          },

          'file.inserted': function (file, response) {
            console.log("✔️ File inserted into editor", file, response);
            // Mark operation as complete
            if (window.currentFroalaController) {
              window.currentFroalaController.markOperationComplete();
            }
          },

          'quote.inserted': function (quote, response) {
            console.log("✔️ Quote inserted into editor", quote, response);
          },

          'hr.inserted': function (hr, response) {
            console.log("✔️ Horizontal rule inserted into editor", hr, response);
          },

          // 'contentChanged': () => {
          //   // Debounced auto-save without interfering with user experience
          //   if (window.currentFroalaController) {
          //     clearTimeout(window.currentFroalaController.autoSaveTimeout);
          //     window.currentFroalaController.autoSaveTimeout = setTimeout(() => {
          //       console.log('🔧 Auto-saving content after 2 seconds of inactivity');
          //       window.currentFroalaController.saveContent(textArea);
          //     }, 2000); // Save after 2 seconds of no changes
          //   }
          // },

          'keydown': (key, event) => {
            const editor = this;
            console.log('⌨️ Keydown event:', key);
            
            if (editor.quickInsert) {
              editor.quickInsert._keepVisible = true;
              editor.quickInsert._allowHide = false;
              
              requestAnimationFrame(() => {
                const quickInsertElement = editor.$box ? editor.$box.find('.fr-quick-insert').get(0) : null;
                if (quickInsertElement) {
                  quickInsertElement.classList.add('fr-visible');
                  quickInsertElement.style.setProperty('display', 'flex', 'important');
                  quickInsertElement.style.setProperty('visibility', 'visible', 'important');
                  quickInsertElement.style.setProperty('opacity', '1', 'important');
                  
                  try {
                    const selection = editor.selection.get();
                    console.log('🔍 Keydown - Selection:', selection);
                    
                    const containerNode = selection?.anchorNode || selection?.startContainer;
                    const offset = selection?.anchorOffset !== undefined ? selection.anchorOffset : selection?.startOffset;
                    
                    if (selection && containerNode && offset !== undefined) {
                      const range = document.createRange();
                      range.setStart(containerNode, offset);
                      range.setEnd(containerNode, offset);
                      const cursorRect = range.getBoundingClientRect();
                      console.log('📐 Keydown - Cursor rect:', cursorRect);
                    } else {
                      console.warn('⚠️ Keydown - No container node or offset:', { containerNode, offset });
                    }
                  } catch (e) {
                    console.error('❌ Keydown - Selection error:', e);
                  }
                  
                  if (editor.quickInsert.updatePosition) {
                    console.log('🔄 Calling Froala updatePosition');
                    editor.quickInsert.updatePosition();
                  } else {
                    console.warn('⚠️ updatePosition method not found');
                  }
                  
                  if (editor.positionQuickInsertRelativeToCursor) {
                    console.log('🔄 Calling custom positionQuickInsertRelativeToCursor');
                    editor.positionQuickInsertRelativeToCursor(quickInsertElement, true);
                  }
                }
              });
            }
          },
          
          'keyup': function() {
            const editor = this;
            if (editor.quickInsert && editor.quickInsert._keepVisible) {
              requestAnimationFrame(() => {
                const quickInsertElement = editor.$box ? editor.$box.find('.fr-quick-insert').get(0) : null;
                if (quickInsertElement) {
                  quickInsertElement.style.setProperty('display', 'flex', 'important');
                  quickInsertElement.style.setProperty('visibility', 'visible', 'important');
                  quickInsertElement.style.setProperty('opacity', '1', 'important');
                  
                  const computedStyle = window.getComputedStyle(quickInsertElement);
                  const hasTop = computedStyle.top && computedStyle.top !== 'auto' && !computedStyle.top.includes('-9999');
                  const hasLeft = computedStyle.left && computedStyle.left !== 'auto' && !computedStyle.left.includes('-9999');
                  
                  // CRITICAL: Always recalculate position based on current cursor
                  // This ensures the button always appears at the same position relative to the cursor
                  if (!quickInsertElement._isPositioning) {
                    quickInsertElement._isPositioning = true;
                    try {
                      if (editor.positionQuickInsertRelativeToCursor) {
                        editor.positionQuickInsertRelativeToCursor(quickInsertElement, true);
                      } else if (typeof positionQuickInsertRelativeToCursor === 'function') {
                        positionQuickInsertRelativeToCursor(quickInsertElement, true);
                      }
                    } finally {
                      quickInsertElement._isPositioning = false;
                    }
                  }
                }
              });
            }
          },

          'input': function () {
            const editor = this;
            if (editor.quickInsert) {
              editor.quickInsert._keepVisible = true;
              editor.quickInsert._allowHide = false;
              
              requestAnimationFrame(() => {
                const quickInsertElement = editor.$box ? editor.$box.find('.fr-quick-insert').get(0) : null;
                if (quickInsertElement && editor.quickInsert._keepVisible) {
                  quickInsertElement.classList.add('fr-visible');
                  quickInsertElement.style.setProperty('display', 'flex', 'important');
                  quickInsertElement.style.setProperty('visibility', 'visible', 'important');
                  quickInsertElement.style.setProperty('opacity', '1', 'important');
                  
                  if (editor.quickInsert.updatePosition) {
                    editor.quickInsert.updatePosition();
                  }
                }
              });
              
              [5, 20, 50, 100].forEach(delay => {
              setTimeout(() => {
                  if (editor.quickInsert && editor.quickInsert._keepVisible) {
                    const quickInsertElement = editor.$box ? editor.$box.find('.fr-quick-insert').get(0) : null;
                    if (quickInsertElement && !quickInsertElement._isPositioning) {
                      const computedStyle = window.getComputedStyle(quickInsertElement);
                      const hasTop = computedStyle.top && computedStyle.top !== 'auto' && !computedStyle.top.includes('-9999');
                      const hasLeft = computedStyle.left && computedStyle.left !== 'auto' && !computedStyle.left.includes('-9999');
                      
                      // CRITICAL: Always recalculate position based on current cursor
                      // This ensures the button always appears at the same position relative to the cursor
                      if (!quickInsertElement._isPositioning) {
                        quickInsertElement._isPositioning = true;
                        try {
                          if (editor.positionQuickInsertRelativeToCursor) {
                            editor.positionQuickInsertRelativeToCursor(quickInsertElement, true);
                          } else if (typeof positionQuickInsertRelativeToCursor === 'function') {
                            positionQuickInsertRelativeToCursor(quickInsertElement, true);
                          }
                        } finally {
                          quickInsertElement._isPositioning = false;
                        }
                      }
                      
                      quickInsertElement.style.setProperty('display', 'flex', 'important');
                      quickInsertElement.style.setProperty('visibility', 'visible', 'important');
                      quickInsertElement.style.setProperty('opacity', '1', 'important');
                      quickInsertElement.classList.add('fr-visible');
                    }
                  }
                }, delay);
              });
            }
          },

          'contentChanged': function () {
            const editor = this;
            if (editor.quickInsert) {
              editor.quickInsert._keepVisible = true;
              editor.quickInsert._allowHide = false;
              
              requestAnimationFrame(() => {
                const quickInsertElement = editor.$box ? editor.$box.find('.fr-quick-insert').get(0) : null;
                if (quickInsertElement && editor.quickInsert._keepVisible) {
                  if (editor.quickInsert.show) {
                    editor.quickInsert.show();
                  }
                  
                  quickInsertElement.classList.add('fr-visible');
                  
                  quickInsertElement.style.setProperty('display', 'flex', 'important');
                  quickInsertElement.style.setProperty('visibility', 'visible', 'important');
                  quickInsertElement.style.setProperty('opacity', '1', 'important');
                  
                  if (quickInsertElement && !quickInsertElement._isPositioning) {
                    quickInsertElement._isPositioning = true;
                    try {
                      if (editor.positionQuickInsertRelativeToCursor) {
                        editor.positionQuickInsertRelativeToCursor(quickInsertElement, true);
                      } else if (typeof positionQuickInsertRelativeToCursor === 'function') {
                        positionQuickInsertRelativeToCursor(quickInsertElement, true);
                      }
                    } finally {
                      quickInsertElement._isPositioning = false;
                    }
                  }
                }
              });
              
              [5, 20, 50, 100].forEach(delay => {
                setTimeout(() => {
                  if (editor.quickInsert && editor.quickInsert._keepVisible) {
                    const quickInsertElement = editor.$box ? editor.$box.find('.fr-quick-insert').get(0) : null;
                    if (quickInsertElement && !quickInsertElement._isPositioning) {
                      const computedStyle = window.getComputedStyle(quickInsertElement);
                      const hasTop = computedStyle.top && computedStyle.top !== 'auto' && !computedStyle.top.includes('-9999');
                      const hasLeft = computedStyle.left && computedStyle.left !== 'auto' && !computedStyle.left.includes('-9999');
                      
                      // CRITICAL: Always recalculate position based on current cursor
                      // This ensures the button always appears at the same position relative to the cursor
                      if (!quickInsertElement._isPositioning) {
                        quickInsertElement._isPositioning = true;
                        try {
                          if (editor.positionQuickInsertRelativeToCursor) {
                            editor.positionQuickInsertRelativeToCursor(quickInsertElement, true);
                          } else if (typeof positionQuickInsertRelativeToCursor === 'function') {
                            positionQuickInsertRelativeToCursor(quickInsertElement, true);
                          }
                        } finally {
                          quickInsertElement._isPositioning = false;
                        }
                      }
                      
                      quickInsertElement.style.setProperty('display', 'flex', 'important');
                      quickInsertElement.style.setProperty('visibility', 'visible', 'important');
                      quickInsertElement.style.setProperty('opacity', '1', 'important');
                      quickInsertElement.classList.add('fr-visible');
                    }
                  }
                }, delay);
              });
            }
          },
          
          'selection.changed': function() {
            const editor = this;
            
            const textArea = editor.$el ? editor.$el.get(0) : null;
            if (!textArea || !textArea.dataset.froalaInitialized) {
              return;
            }
            
            try {
              const selection = editor.selection.get();
              const containerNode = selection?.anchorNode || selection?.startContainer;
              const offset = selection?.anchorOffset !== undefined ? selection.anchorOffset : selection?.startOffset;
              
              if (!selection || !containerNode || offset === undefined) {
                return;
              }
              
              const editorBox = editor.$box ? editor.$box.get(0) : null;
              if (editorBox && !editorBox.contains(containerNode)) {
                return;
              }
            } catch (e) {
              return;
            }
            
            if (editor.quickInsert) {
              editor.quickInsert._keepVisible = true;
              editor.quickInsert._allowHide = false;
              
              requestAnimationFrame(() => {
                const quickInsertElement = editor.$box ? editor.$box.find('.fr-quick-insert').get(0) : null;
                if (quickInsertElement && !quickInsertElement._isPositioning) {
                  quickInsertElement.classList.add('fr-visible');
                  quickInsertElement.style.setProperty('display', 'flex', 'important');
                  quickInsertElement.style.setProperty('visibility', 'visible', 'important');
                  quickInsertElement.style.setProperty('opacity', '1', 'important');
                  
                  quickInsertElement._isPositioning = true;
                  try {
                    if (editor.positionQuickInsertRelativeToCursor) {
                      editor.positionQuickInsertRelativeToCursor(quickInsertElement, true);
                    } else if (typeof positionQuickInsertRelativeToCursor === 'function') {
                      positionQuickInsertRelativeToCursor(quickInsertElement, true);
                    }
                  } finally {
                    quickInsertElement._isPositioning = false;
                  }
                }
              });
              
              try {
                const selection = editor.selection.get();
                if (selection && selection.startContainer) {
                  let lineElement = selection.startContainer;
                  if (lineElement.nodeType === Node.TEXT_NODE) {
                    lineElement = lineElement.parentElement;
                  }
                  
                  while (lineElement && lineElement !== editor.$box.get(0)) {
                    if (lineElement.tagName === 'P' || lineElement.tagName === 'DIV' || 
                        lineElement.classList.contains('fr-element')) {
                      break;
                    }
                    lineElement = lineElement.parentElement;
                  }
                  
                  const textContent = lineElement ? (lineElement.textContent || '') : '';
                  const isOnExistingText = textContent.trim().length > 0;
                  
                  if (isOnExistingText) {
                    editor.quickInsert._keepVisible = true;
                    editor.quickInsert._allowHide = false;
                  }
                }
              } catch (e) {
                editor.quickInsert._keepVisible = true;
                editor.quickInsert._allowHide = false;
              }
              
              if (editor.quickInsert._keepVisible) {
                setTimeout(() => {
                  if (editor.quickInsert.show) {
                    editor.quickInsert.show();
                  }
                  const quickInsertElement = editor.$box ? editor.$box.find('.fr-quick-insert').get(0) : null;
                  if (quickInsertElement) {
                    quickInsertElement.classList.add('fr-visible');
                    
                    requestAnimationFrame(() => {
                      // CRITICAL: Always recalculate position based on current cursor
                      if (editor.positionQuickInsertRelativeToCursor) {
                        editor.positionQuickInsertRelativeToCursor(quickInsertElement, true);
                      } else if (typeof positionQuickInsertRelativeToCursor === 'function') {
                        positionQuickInsertRelativeToCursor(quickInsertElement, true);
                      }
                    });
                    
                      const style = quickInsertElement.getAttribute('style') || '';
                      if (style.includes('top: -9999px') || style.includes('top:-9999px') ||
                          style.includes('left: -9999px') || style.includes('left:-9999px')) {
                        quickInsertElement.style.removeProperty('top');
                        quickInsertElement.style.removeProperty('left');
                        // CRITICAL: Always recalculate position based on current cursor
                        if (editor.positionQuickInsertRelativeToCursor) {
                          editor.positionQuickInsertRelativeToCursor(quickInsertElement, true);
                        } else if (typeof positionQuickInsertRelativeToCursor === 'function') {
                          positionQuickInsertRelativeToCursor(quickInsertElement, true);
                        }
                      }
                    
                    quickInsertElement.style.display = 'flex';
                    quickInsertElement.style.visibility = 'visible';
                    quickInsertElement.style.opacity = '1';
                    
                    // CRITICAL: Always recalculate position based on current cursor
                    if (editor.positionQuickInsertRelativeToCursor) {
                      editor.positionQuickInsertRelativeToCursor(quickInsertElement, true);
                    } else if (typeof positionQuickInsertRelativeToCursor === 'function') {
                      positionQuickInsertRelativeToCursor(quickInsertElement, true);
                    }
                }
              }, 10);
              }
            }
          },
          
          'mouseup': function() {
            const editor = this;
            
            const textArea = editor.$el ? editor.$el.get(0) : null;
            if (!textArea || !textArea.dataset.froalaInitialized) {
              return;
            }
            
            try {
              const selection = editor.selection.get();
              const containerNode = selection?.anchorNode || selection?.startContainer;
              const offset = selection?.anchorOffset !== undefined ? selection.anchorOffset : selection?.startOffset;
              
              if (!selection || !containerNode || offset === undefined) {
                return;
              }
              
              const editorBox = editor.$box ? editor.$box.get(0) : null;
              if (editorBox && !editorBox.contains(containerNode)) {
                return;
              }
            } catch (e) {
              return;
            }
            
            if (editor.quickInsert) {
              editor.quickInsert._keepVisible = true;
              editor.quickInsert._allowHide = false;
              
              requestAnimationFrame(() => {
                const quickInsertElement = editor.$box ? editor.$box.find('.fr-quick-insert').get(0) : null;
                if (quickInsertElement && !quickInsertElement._isPositioning) {
                  quickInsertElement.classList.add('fr-visible');
                  quickInsertElement.style.setProperty('display', 'flex', 'important');
                  quickInsertElement.style.setProperty('visibility', 'visible', 'important');
                  quickInsertElement.style.setProperty('opacity', '1', 'important');
                  
                  quickInsertElement._isPositioning = true;
                  try {
                    if (editor.positionQuickInsertRelativeToCursor) {
                      editor.positionQuickInsertRelativeToCursor(quickInsertElement, true);
                    } else if (typeof positionQuickInsertRelativeToCursor === 'function') {
                      positionQuickInsertRelativeToCursor(quickInsertElement, true);
                    }
                  } finally {
                    quickInsertElement._isPositioning = false;
                  }
                }
              });
              
              setTimeout(() => {
                try {
                  const selection = editor.selection.get();
                  if (selection && selection.startContainer) {
                    let lineElement = selection.startContainer;
                    if (lineElement.nodeType === Node.TEXT_NODE) {
                      lineElement = lineElement.parentElement;
                    }
                    
                    while (lineElement && lineElement !== editor.$box.get(0)) {
                      if (lineElement.tagName === 'P' || lineElement.tagName === 'DIV' || 
                          lineElement.classList.contains('fr-element')) {
                        break;
                      }
                      lineElement = lineElement.parentElement;
                    }
                    
                    const textContent = lineElement ? (lineElement.textContent || '') : '';
                    const isOnExistingText = textContent.trim().length > 0;
                    
                    if (isOnExistingText) {
                      editor.quickInsert._keepVisible = true;
                      editor.quickInsert._allowHide = false;
                      
                      if (editor.quickInsert._originalShow) {
                        editor.quickInsert._originalShow();
                      }
                      
                      const quickInsertElement = editor.$box ? editor.$box.find('.fr-quick-insert').get(0) : null;
                      if (quickInsertElement) {
                        quickInsertElement.classList.add('fr-visible');
                        quickInsertElement.style.display = 'flex';
                        quickInsertElement.style.visibility = 'visible';
                        quickInsertElement.style.opacity = '1';
                        
                        // CRITICAL: Always recalculate position based on current cursor
                        if (editor.positionQuickInsertRelativeToCursor) {
                          editor.positionQuickInsertRelativeToCursor(quickInsertElement, true);
                        } else if (typeof positionQuickInsertRelativeToCursor === 'function') {
                          positionQuickInsertRelativeToCursor(quickInsertElement, true);
                        }
                      }
                    }
                  }
                } catch (e) {
                  console.log('Error checking text on mouseup:', e);
                }
              }, 50);
            }
          },

          'commands.after': function (cmd) {
            if (cmd === 'findReplaceButton') {
              setTimeout(() => {
                const editorBox = this.$box.get(0);
                const frView = document.querySelector('.fr-element.fr-view');

                // Safely get clean HTML without highlight spans
                const getCleanHtml = () =>
                  frView?.innerHTML
                    .replace(/<span class="fr-find-replace-highlight">/g, "")
                    .replace(/<span class="fr-find-replace-highlight fr-find-replace-current-highlight">/g, "")
                    .replace(/<\/span>/g, "");

                const updateTextArea = () => {
                  if (textArea && frView) {
                    textArea.value = getCleanHtml();
                  }
                };

                // Map buttons to their selectors
                const buttons = {
                  findBtn: '.fr-find-btn',
                  replaceBtn: '.fr-replace-btn',
                  replaceAllBtn: '.fr-replace-all-btn',
                  closeBtn: '.fr-close-btn',
                  advancedOptionBtn: '.fr-toggle-advanced-options',
                  arrowDownBtn: '.fa.fa-chevron-down',
                  arrowUpBtn: '.fa.fa-chevron-up'
                };

                Object.entries(buttons).forEach(([key, selector]) => {
                  const btn = editorBox.querySelector(selector);
                  if (btn) {
                    btn.addEventListener('click', (e) => {
                      e.preventDefault();
                      console.log("Updating from:", selector);
                      updateTextArea();
                    });
                  }
                });
              }, 100); // Wait for DOM to be ready
            } else {
              // Restore focus after any other command execution
              setTimeout(() => {
                this.events.focus();
                this.selection.restore();
              }, 10);
            }
          },

          'image.beforeUpload': function (files) {
            console.log("🖼️ Image selected for upload:", files);
            // Mark operation as started
            if (window.currentFroalaController) {
              window.currentFroalaController.operationInProgress = true;
            }
            customFileUpload(files[0], this, "image");
            return false; // Prevent Froala's default upload
          },

          'image.beforePasteUpload': function (img) {
            console.log("🛑 Preventing default pasted image insertion");
            detectAndUploadBase64Images(this);
            return false; // Prevent automatic base64 image insertion
          },

          'image.inserted': function ($img, response) {
            console.log("✔️ Image inserted into editor", $img, response);
            // Resize pasted images to max 500px width
            resizeImageToMaxWidth($img, 500);
          },

          'paste.after': function () {
            // Handle URL to link conversion and image resize after paste
            const editor = this;
            setTimeout(() => {
              // Convert URLs to links
              convertUrlsToLinks(editor);
              // Resize any pasted images
              resizePastedImages(editor, 500);
            }, 100);
          },

          'file.beforeUpload': function (files) {
            console.log("📄 File selected for upload:", files);
            customFileUpload(files[0], this, "file");
            return false; // Prevent Froala's default upload
          },

          'image.inserted': function ($img, response) {
            console.log("✔️ Image inserted into editor", $img, response);
          },

          'focus': function () {
            // Don't force cursor to end - preserve user's cursor position
            if (window.currentFroalaController) {
              window.currentFroalaController.showMergeTagsAsTags(this);
            }
          },

          'blur': function () {
            const editor = this;
            if (editor.quickInsert) {
              const allQuickInsertElements = editor.$box ? editor.$box.find('.fr-quick-insert').toArray() : [];
              
              allQuickInsertElements.forEach(quickInsertElement => {
                const contentEditable = editor.$box ? editor.$box.find('[contenteditable="true"]').get(0) : null;
                if (contentEditable && contentEditable.contains(quickInsertElement)) {
                  console.warn('⚠️ Quick insert button found in content editable, removing it');
                  quickInsertElement.remove();
                  return;
                }
                
                quickInsertElement.style.setProperty('display', 'none', 'important');
                quickInsertElement.style.setProperty('visibility', 'hidden', 'important');
                quickInsertElement.style.setProperty('opacity', '0', 'important');
                quickInsertElement.classList.remove('fr-visible');
                quickInsertElement.classList.add('fr-hidden');
              });
              
              editor.quickInsert._keepVisible = false;
              editor.quickInsert._allowHide = true;
            }
            
            if (window.currentFroalaController) {
              window.currentFroalaController.replaceTagsWithValues(this);
            }
          },

          'destroy': function () {
            // Remove global selectionchange listener on destroy
            const editor = this;
            if (editor._selectionChangeHandler) {
              document.removeEventListener('selectionchange', editor._selectionChangeHandler);
              delete editor._selectionChangeHandler;
            }
            
            delete textArea.dataset.froalaInitialized; // Reset on destroy
          }
        }
      });
    }, 0);
  }

  saveContent(textArea) {
    console.log(textArea.value);
    const container = textArea.closest('.rich-text-container');
    if (!container) return;
    
    const { taskId, accountId, projectId, taskListId, blockId, contentType } = container.dataset;
    const content = textArea.value;

    const blockElement = document.querySelector(`[data-block-id="${blockId}"]`);
    const button = blockElement?.querySelector('button');
    if (button) {
      button.classList.add('hidden');
    }
    
    fetch(`/${accountId}/projects/${projectId}/task_lists/${taskListId}/tasks/${taskId}/task_blocks/${blockId}/update_content`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({ 
        task_block: { [contentType]: content },
        skip_broadcast: false // Always broadcast - the channel will skip updates for the editing user
      })
    })
    .then(response => response.json())
    .then(data => {
      console.log("Content saved:", data);
      // Stop editing after successful save
      window.activeEditingManager.stopEditing(this.blockId, 500);
   
      if (button) {
        button.classList.remove('hidden');
      }
    })
    .catch(error => {
      console.error("Error saving content:", error);
      // Stop editing on error
   
      if (button) {
        button.classList.remove('hidden');
      }
      window.activeEditingManager.stopEditing(this.blockId, 1000);
    });
  }

  fetchMergeTags(editorWrapper) {
    return new Promise((resolve, reject) => {
      const textArea = editorWrapper.querySelector(".froala");
      const container = textArea.closest('.rich-text-container');
      if (!container) return reject(new Error("Rich text container not found."));
      
      const { accountId, projectId, taskListId, taskId, blockId, processTemplateId, contentType } = container.dataset;
      let url;
      
      if (taskId) {
        url = `/${accountId}/projects/${projectId}/task_lists/${taskListId}/tasks/${taskId}/task_blocks/get_liquid_tags?block_id=${blockId}&content_type=${contentType}`;
      } else if (processTemplateId) {
        url = `/${accountId}/process_templates/${processTemplateId}/get_liquid_tags?block_id=${blockId}&content_type=${contentType}`;
      } else if (taskListId) {
        url = `/${accountId}/projects/${projectId}/task_lists/${taskListId}/get_liquid_tags`;
      } else if (projectId) {
        url = `/${accountId}/projects/${projectId}/get_liquid_tags`;
      } else {
        url = `/${accountId}/client_assignments/get_liquid_tags`;
      }
      
      fetch(url, {
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name=\"csrf-token\"]").content
        }
      })
      .then(response => response.ok ? response.json() : Promise.reject(response.status))
      .then(data => resolve(data.map(tag => ({ value: tag[1], title: tag[0] }))))
      .catch(reject);
    });
  }



  showMergeTagsAsTags(editor) {
    if (!this.mergeTagValues || this.mergeTagValues.size === 0) {
      return;
    }

    let content = editor.html.get();

    this.mergeTagValues.forEach((value, tag) => {
      const escapedValue = value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(escapedValue, 'g');
      const newContent = content.replace(regex, tag);

      if (newContent !== content) {
        content = newContent;
      }
    });

    if (content !== editor.html.get()) {
      editor.html.set(content);
    }
  }

  replaceTagsWithValues(editor) {
    if (!this.mergeTagValues || this.mergeTagValues.size === 0) {
      return;
    }

    let content = editor.html.get();
    let hasChanges = false;

    this.mergeTagValues.forEach((value, tag) => {
      if (content.includes(tag)) {
        const escapedTag = this.escapeRegExp(tag);
        content = content.replace(new RegExp(escapedTag, 'g'), value);
        hasChanges = true;
      }
    });

    if (hasChanges) {
      editor.html.set(content);
    }
  }

  escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}

function customFileUpload(file, editor, type) {
  const reader = new FileReader();
  reader.onload = function (event) {
    const formData = new FormData();
    
    // Get CSRF token with error handling
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    if (!csrfToken) {
      console.error('❌ CSRF token not found');
      return;
    }
    
    formData.append("authenticity_token", csrfToken);
    formData.append("file", file);
    
    // Get upload URL with fallback
    const uploadURL = editor.opts?.imageUploadURL || '/froala_file_uploader';
    console.log(`📤 Uploading ${type} to: ${uploadURL}`);
    
    fetch(uploadURL, {
      method: "POST",
      body: formData
    })
    .then(response => {
      console.log(`📤 Upload response status: ${response.status}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return response.json();
    })
    .then(data => {
      console.log("📤 Upload response:", data);
      if (data.success) {
        editor.selection.restore();
        if (type === "image") {
          // Insert a placeholder image with the SGID stored in a data attribute
          const imageTag = `<img src="${data.link}" data-attachment-sgid="${data.attachment_sgid}" />`;
          editor.html.insert(imageTag);
          
          const textArea = editor.el;
          if (textArea) {
            const container = textArea.closest('.rich-text-container');
            if (container) {
              const { taskId, blockId, contentType } = container.dataset;
              if (taskId && blockId && contentType && window.currentFroalaController) {
                textArea.value = editor.html.get();
                setTimeout(() => {
                  window.currentFroalaController.saveContent(textArea);
                }, 100);
              }
            }
          }
        }
        console.log("✅ File uploaded successfully:", data);
        
        // Mark operation as complete
        if (window.currentFroalaController) {
          window.currentFroalaController.markOperationComplete();
        }
      } else {
        console.error("❌ Upload failed:", data.error);
      }
    })
    .catch(error => {
      console.error("❌ Error uploading file:", error);
      
      // Mark operation as complete even on error
      if (window.currentFroalaController) {
        window.currentFroalaController.markOperationComplete();
      }
    });
  };
  reader.readAsDataURL(file);
}

function detectAndUploadBase64Images(editor) {
  const images = editor.el.querySelectorAll("img");
  images.forEach((img) => {
    if (img.src.startsWith("data:image")) {
      uploadBase64Image(img.src)
        .then((uploadedImageUrl) => {
          img.src = uploadedImageUrl;
          // Resize uploaded image to max 500px width
          resizeImageToMaxWidth(img, 500);
        })
        .catch((error) => console.error("Image upload failed", error));
    } else {
      // Resize existing images to max 500px width
      resizeImageToMaxWidth(img, 500);
    }
  });
}

// Function to resize image to maximum width while maintaining aspect ratio
function resizeImageToMaxWidth(img, maxWidth) {
  if (!img || !img.complete) {
    // Wait for image to load
    img.onload = function() {
      resizeImageToMaxWidth(img, maxWidth);
    };
    return;
  }
  
  if (img.naturalWidth > maxWidth) {
    const aspectRatio = img.naturalHeight / img.naturalWidth;
    img.style.maxWidth = maxWidth + 'px';
    img.style.width = maxWidth + 'px';
    img.style.height = 'auto';
    // Remove width/height attributes to use CSS instead
    img.removeAttribute('width');
    img.removeAttribute('height');
  }
}

// Function to resize all pasted images in the editor
function resizePastedImages(editor, maxWidth) {
  const images = editor.el.querySelectorAll("img");
  images.forEach((img) => {
    resizeImageToMaxWidth(img, maxWidth);
  });
}

function isPartOfEmail(text, matchIndex, url) {
  if (matchIndex < 0 || matchIndex >= text.length) return false;
  
  const before = matchIndex > 0 ? text[matchIndex - 1] : '';
  
  
  const lookbackStart = Math.max(0, matchIndex - 100);
  const textBefore = text.substring(lookbackStart, matchIndex);
  
  
  if (textBefore.includes('@')) {
    // Extract potential email part before @
    const emailPart = textBefore.substring(textBefore.lastIndexOf('@') + 1);
    // If the URL starts right after @, it's part of an email
    if (emailPart.trim().length === 0 || emailPart.trim() === url.substring(0, emailPart.trim().length)) {
      return true;
    }
  }
  
  
  return before === '@';
}


function isValidDomainUrl(url) {
  
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return true;
  }
  
  
  let domain = url.replace(/^https?:\/\//, '').replace(/^www\./, '');
  
  
  domain = domain.split('/')[0];
  
  
  if (!domain.includes('.')) {
    return false;
  }
  

  const parts = domain.split('.');
  
  
  if (parts.length < 2) {
    return false;
  }
  
 
  const compoundTlds = new Set([
    'co.uk', 'com.au', 'co.nz', 'co.in', 'com.pk', 'co.za', 'com.br', 'com.mx',
    'co.jp', 'com.cn', 'co.kr', 'com.sg', 'com.hk', 'com.tw', 'com.my', 'com.ph',
    'co.th', 'com.vn', 'com.id', 'co.id', 'net.au', 'org.uk', 'gov.uk', 'ac.uk',
    'ltd.uk', 'plc.uk', 'net.nz', 'org.nz', 'gov.au', 'edu.au', 'asn.au', 'id.au'
  ]);
  
  
  if (parts.length >= 2) {
    const lastTwo = parts.slice(-2).join('.').toLowerCase();
    if (compoundTlds.has(lastTwo)) {
      return true;
    }
  }
  
  // Get the TLD (last part)
  const tld = parts[parts.length - 1].toLowerCase();
  
  
  const validCcTlds = new Set([
    'us', 'uk', 'pk', 'de', 'fr', 'in', 'au', 'ca', 'cn', 'jp', 'sa', 'ae', 'eu', 'it', 'es', 'nl',
    'se', 'no', 'fi', 'dk', 'ch', 'at', 'be', 'pl', 'cz', 'ie', 'pt', 'gr', 'ro', 'hu', 'bg', 'hr',
    'sk', 'si', 'ee', 'lv', 'lt', 'lu', 'mt', 'cy', 'co', 'mx', 'br', 'ar', 'cl', 'pe', 'za',
    'ng', 'eg', 'ke', 'ma', 'tn', 'dz', 'ly', 'sd', 'et', 'ug', 'tz', 'gh', 'ci', 'sn', 'cm', 'ao',
    'mz', 'zw', 'bw', 'na', 'zm', 'mg', 'mu', 'sc', 'km', 'dj', 'er', 'so', 'rw', 'bi', 'td', 'ne',
    'ml', 'bf', 'sl', 'lr', 'tg', 'bj', 'mr', 'gm', 'gw', 'gn', 'cv', 'st', 'gq', 'ga', 'cg', 'cd',
    'cf', 'td', 'kh', 'la', 'mm', 'bn', 'sg', 'my', 'id', 'ph', 'vn', 'th', 'bd', 'lk', 'mv', 'np',
    'bt', 'mn', 'kz', 'uz', 'tj', 'tm', 'kg', 'af', 'ir', 'iq', 'sy', 'jo', 'lb', 'il', 'ps', 'ye',
    'om', 'qa', 'bh', 'kw', 'ae', 'tr', 'az', 'ge', 'am', 'by', 'ua', 'md', 'ru', 'is', 'fo', 'gl',
    'gi', 'ad', 'mc', 'sm', 'va', 'li', 'je', 'gg', 'im', 'ax', 'io', 'tv', 'cc', 'ws', 'as', 'gu',
    'mp', 'pr', 'vi', 'vg', 'ky', 'bm', 'fk', 'gs', 'sh', 'tc', 'pf', 'nc', 'pn', 'nf', 'cx', 'hm',
    'aq', 'tf', 'gf', 'gp', 'mq', 're', 'yt', 'pm', 'bl', 'mf', 'sx', 'cw', 'bq', 'aw', 'sj', 'eh'
  ]);
  
  // Only allow known valid ccTLDs, not all 2-letter strings
  if (tld.length === 2) {
    return validCcTlds.has(tld);
  }
  
  // Generic TLDs (3+ characters)
  const validGenericTlds = new Set([
    'com', 'org', 'net', 'edu', 'gov', 'mil', 'int', 'info', 'biz', 'name', 'pro',
    'aero', 'coop', 'museum', 'jobs', 'mobi', 'travel', 'tel', 'asia', 'cat',
    'xxx', 'post', 'geo', 'app', 'dev', 'io', 'tech', 'online', 'site', 'website',
    'store', 'shop', 'blog', 'news', 'media', 'tv', 'me', 'cc', 'ws', 'ly', 'ai'
  ]);
  
  // Check if TLD is in the generic TLD list
  return validGenericTlds.has(tld);
}

// Function to convert URLs in text to links
function convertUrlsToLinks(editor) {
  const editorElement = editor.el;
  const textNodes = getTextNodes(editorElement);
  
  // Unicode-safe alphanumeric check (letters and numbers from any language)
  const isAlphaNumericUnicode = (char) => /[\p{L}\p{N}]/u.test(char);
  
  textNodes.forEach((textNode) => {
    const text = textNode.textContent;
    
   
    const urlRegex = /(https?:\/\/[^\s<>"']+|www\.(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(?:\/[^\s<>"']*)?|(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(?:\/[^\s<>"']*)?)/gi;
    
    let match;
    const matches = [];
    const matchIndices = [];
    
    // Collect all matches with their indices
    while ((match = urlRegex.exec(text)) !== null) {
      let url = match[0];
      const matchIndex = match.index;
      
      url = url.replace(/[.,;:!?]+$/, '');
      
      const charBefore = matchIndex > 0 ? text[matchIndex - 1] : '';
      const charAfter = matchIndex + url.length < text.length ? text[matchIndex + url.length] : '';
      
      // Reject if glued to letters/numbers (must be separated by space, punctuation, or start/end)
      if (charBefore && isAlphaNumericUnicode(charBefore)) {
        continue; // Skip matches that are mid-word
      }
      if (charAfter && isAlphaNumericUnicode(charAfter)) {
        continue; // Skip matches that are followed by word characters
      }
      
      matches.push(url);
      matchIndices.push(matchIndex);
    }
    
    if (matches.length > 0) {
      let newHTML = text;
      
      // Process matches in reverse order to preserve indices
      for (let i = matches.length - 1; i >= 0; i--) {
        const url = matches[i];
        const matchIndex = matchIndices[i];
        
        // Validation flow (correct order - DO THIS EXACTLY):
        // 1. Reject if already inside a link
        let parent = textNode.parentNode;
        let isInsideLink = false;
        while (parent && parent !== editorElement) {
          if (parent.tagName === 'A') {
            isInsideLink = true;
            break;
          }
          parent = parent.parentNode;
        }
        
        if (isInsideLink) continue;
        
        if (isPartOfEmail(text, matchIndex, url)) continue;
        
        const charBefore = matchIndex > 0 ? text[matchIndex - 1] : '';
        const charAfter = matchIndex + url.length < text.length ? text[matchIndex + url.length] : '';
        if (charBefore && isAlphaNumericUnicode(charBefore)) continue;
        if (charAfter && isAlphaNumericUnicode(charAfter)) continue;
        
        
        const isValidUrl = isValidDomainUrl(url);
        
        if (isValidUrl) {
          // Ensure URL has protocol
          let fullUrl = url;
          if (!url.startsWith('http://') && !url.startsWith('https://')) {
            fullUrl = 'https://' + url;
          }
          
          // Replace URL with link (open in new window by default)
          const linkHTML = `<a href="${fullUrl}" target="_blank" rel="noopener noreferrer">${url}</a>`;
          
          const beforeMatch = newHTML.substring(0, matchIndex);
          const afterMatch = newHTML.substring(matchIndex + url.length);
          newHTML = beforeMatch + linkHTML + afterMatch;
        }
      }
      
      if (newHTML !== text) {
        // Create a temporary container to parse the HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = newHTML;
        
        // Replace the text node with the new content
        const parent = textNode.parentNode;
        while (tempDiv.firstChild) {
          parent.insertBefore(tempDiv.firstChild, textNode);
        }
        parent.removeChild(textNode);
      }
    }
  });
  
  // Update Froala's internal HTML
  editor.html.set(editor.html.get());
}

// Helper function to get all text nodes in an element
function getTextNodes(element) {
  const textNodes = [];
  const walker = document.createTreeWalker(
    element,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode: function(node) {
        // Skip text nodes that are inside links
        let parent = node.parentNode;
        while (parent && parent !== element) {
          if (parent.tagName === 'A') {
            return NodeFilter.FILTER_REJECT;
          }
          parent = parent.parentNode;
        }
        return NodeFilter.FILTER_ACCEPT;
      }
    }
  );
  
  let node;
  while (node = walker.nextNode()) {
    textNodes.push(node);
  }
  
  return textNodes;
}

async function uploadBase64Image(base64Data) {
  const response = await fetch("/upload_binary_image", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content,
    },
    body: JSON.stringify({ image: base64Data }),
  });
  if (!response.ok) throw new Error("Failed to upload image");
  const data = await response.json();
  return data.url;
}



